import { beforeJoin } from 'trtc-js-sdk-core/src/middleware/join';
import { leaveInnerEvent } from 'trtc-js-sdk-core/src/middleware/leave';
import { changeRemoteTrackState } from 'trtc-js-sdk-core/src/middleware/subscribe';
import eventLogger from 'trtc-js-sdk-core/src/utils/log/event-log';
import {
  BandWidthOption,
  BannedReason,
  ConnectionEvent,
  ErrorCode,
  ErrorName,
  getEvents,
  innerVersion,
  IRoom,
  KIBANA_EVENT,
  LocalAudioTrack,
  LocalVideoTrack,
  MediaType,
  SceneNumber, PEER_CONNECTION_STATE,
  RemoteStreamType,
  RoomEvent,
  RoomOption,
  rtcDetector,
  RtcError,
  Scene,
  SDP_SEMANTICS_PLAN_B,
  SDP_SEMANTICS_UNIFIED_PLAN, SUBSCRIBE_SMALL_RETRY_COUNT,
  User,
  UserManager,
  UserManagerEvent,
  UserRole,
  UserRoleNumber,
  utils as Util,
  version,
  getRetryCount,
  CONNECTION_CLOSED_REASON,
  NAME,
  checkPendingPromise,
  CROSS_ROOM_BIT,
  TIMER_TYPE,
  setNetworkTimeOffset,
  ASR_ROBOT_FROM_TYPE,
  LocalScreenTrack,
  FirewallDetector,
  FirewallDetectorEvent,
  utils
} from 'trtc-js-sdk-core';
import SignalChannel, { getActiveKeepAliveSignalChannel } from './signal/signal-channel';
import DownlinkConnection, { getSubscribedKeys } from './connection/downlink-connection';
import UplinkConnection from './connection/uplink-connection';
import Stats from './metrics/stats';
import NetworkQuality from './metrics/network-quality';
import { generateErrorMessage } from 'trtc-js-sdk-core/src/common/error-generator';
import innerEmitter from 'trtc-js-sdk-core/src/inner-emitter/inner-emitter';
import INNER_EVENT from 'trtc-js-sdk-core/src/inner-emitter/inner-event';
import timer from 'trtc-js-sdk-core/src/utils/timer';
import { ChannelEvent, SignalCommand, SignalEvent } from './signal/constant';
import Connection, { isPeerConnectionConnectedBefore } from './connection/connection';
import { BaseRoom, RoomState } from 'trtc-js-sdk-core/src/room-base';
import { addCallQueueValidArgs, addCallQueue, clearCallQueue, removeCallQueue } from 'trtc-js-sdk-core/src/utils/decorators/api-call-queue';
import {
  StartMixTranscodeData,
  StopMixTranscodeData,
  StartPublishGivenCDNData,
  StartPublishTencentCDNData,
  StopPublishGivenCDNData,
  StopPublishTencentCDNData,
  ConnectionState,
  ILocalTrack,
  IRemoteTrack,
  AbilityStatusReportData,
  CustomMessageData,
  SendWaitForResponseWithRetryParams,
  VideoCodec,
  StartPublishMixStreamToRoomData,
  UpdatePublishMixStreamToRoomData,
  StopPublishMixStreamToRoomData,
  PlayoutDelay,
  JitterBufferDelay,
  SignalResponse,
  SSRCGroup,
  PublishedUser,
  LatencyLevel
} from 'trtc-js-sdk-core/src/enums';
import { ChangeState, FSM } from 'afsm';
import { addPromiseRetry, removePromiseRetry } from 'trtc-js-sdk-core/src/utils/decorators/promise-retry';
import { delay, getMuteStateFromFlag, getNumNetworkType, getReconnectionTimeout, isBoolean, isNumber, performanceNow, stringify } from 'trtc-js-sdk-core/src/utils/utils';
import SinglePeerConnection, { SPC_EVENT, buildSSRCGroup } from './connection/single-peerconnection/single-peerconnection';
import UplinkConnectionSPC from './connection/single-peerconnection/uplink-connection-spc';
import DownlinkConnectionSPC from './connection/single-peerconnection/downlink-connection-spc';
import { IS_GET_SYNCHRONIZATION_SOURCES_SUPPORTED, IS_INSERTABLE_STREAM_SUPPORTED, IS_JITTER_BUFFER_TARGET_SUPPORTED, IS_SPC_SUPPORTED, isGetSendersSupported, isReplaceTrackSupported, isWebCodecsSupported } from 'trtc-js-sdk-core/src/common/rtc-detection';
import { KeyPointConnectionType } from 'trtc-js-sdk-core/src/manager/key-point-manager';
import { beforePublish, beforeUnpublish } from 'trtc-js-sdk-core/src/middleware/publish';
import { setIsNeedToSchedule } from 'trtc-js-sdk-core/src/utils/schedule';
import * as eventManager from 'trtc-js-sdk-core/src/manager/event-manager';
import { ClientAbility } from './connection/single-peerconnection/sdp';
import { getDeviceModel, getTerminalType } from 'trtc-js-sdk-core/src/utils/environment';
import { CustomMessageManager, CustomMessageManagerEvent } from './signal/custom-message-manager';
import { after, catchError } from 'trtc-js-sdk-core/src/utils/decorators/middleware';
import HealthDetector from './metrics/health-detector';
import kvStatManager, { KV_REPORT_KEY_NETWORK, KV_REPORT_KEY_VIDEO_ENCODE, KV_REPORT_KEY_WHIP } from 'trtc-js-sdk-core/src/manager/kv-stat-manager';
import VolumeMeter from 'trtc-js-sdk-core/src/common/volume-meter/volume-meter';
import { SwitchRoomConfig } from '../../api-cloud-next/src/type';

import { WhipPullOptions, WhipPullRequestBody, WhipPullResult } from './type/type';

const { isString, isUndefined, getNetworkType, isEmpty } = Util;

export class Room extends BaseRoom implements IRoom {
  // ———— TRTC 应用相关属性 ————
  private _businessInfo?: string;
  userManager: UserManager<DownlinkConnection | DownlinkConnectionSPC>;
  private _version!: string;
  private _heartbeat = -1;
  private _lastHeartBeatTime = -1;
  private _stats: Stats;
  private _joinTimeout = -1;
  private _firstPublishedList: any[] | null = null;
  private _joinReject: ((reason?: any) => void) | null = null;
  private _isRelayChanged = false;
  // ———— 功能属性 ————
  // SDP 规范类型
  sdpSemantics!: string;
  // 记录 join 参数,用于重进房
  signalChannel: SignalChannel | null = null;
  // private _notPublishWithoutH264Supported = false;
  // private _notSubscribeWithoutH264Supported = false;
  uplinkConnection: UplinkConnection | UplinkConnectionSPC | null = null;
  singlePC: SinglePeerConnection | null = null;
  enableSPC = IS_SPC_SUPPORTED;
  // 用户对远端流订阅状态记录
  // Map Map<userId, {
  //       stream: remoteStream,
  //       options: {video, smallVideo},
  //       isSubscribing: false,
  //       reSubscribeCount: 10
  //     }>
  private _changeBigSmallRecords: Map<string, { isSubscribing: boolean, reSubscribeCount: number; options: { video: boolean, smallVideo: boolean } }> = new Map();
  // 指标统计监控
  networkQuality?: NetworkQuality;
  // 客户手动在 TRTC.createClient 设置 iceTransportPolicy
  private _iceTransportPolicy: RTCIceTransportPolicy;
  // 是否强制走 relay 模式
  forceRelay = false;
  // 客户通过 client.setTurnServer 设置
  private _turnServers: RTCIceServer[] = [];
  // 进房信令返回的 iceServers
  private _iceServersFromJoin?: RTCIceServer[];

  // user list 同步
  private _syncUserListInterval = -1;
  private _smallStreamConfig = {
    bitrate: 100, // 小流视频帧的最高码率
    frameRate: 15, // 小流视频帧的帧率
    height: 120, // 小流视频帧的高
    width: 160 // 小流视频帧的宽
  };
  // SEI
  enableSEI?: boolean = false;
  // 是否开启音量回调
  private _enableAudioVolumeEvaluation = false;
  private _audioVolumeIntervalId = 0;
  // 预留隐藏参数，支持开启一个房间推多个辅流
  private _enableMultiAuxStream = false;
  // ———— 转推、旁路、混流 ————
  // 纯音频模式
  private _pureAudioPushMode = false;

  private _customMessageManager?: CustomMessageManager;
  private _enableDataChannel = false;
  private _enableDtx = false;
  preferHW = false;
  healthDetector: HealthDetector;
  playoutDelay?: PlayoutDelay;
  jitterBufferDelay?: JitterBufferDelay;
  private _updateAudioLevelTaskId = -1;
  switchRoomSubedReq?: Promise<any>;
  resolveSwitchRoomSubedReq: (value?: unknown) => void;
  enableVolumeControlInIOS?: boolean;
  capturedLocalMainAudioTrack?: LocalAudioTrack;
  capturedLocalMainVideoTrack?: LocalVideoTrack;
  capturedLocalAuxVideoTrack?: LocalScreenTrack;
  PRECONNECT_EXPIRED_TIME = 5 * 60 * 1000; // 5 min
  PRECONNECT_TIMEOUT = 10 * 1000; // 10s preconnect promise timeout
  private preconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  // 防火墙受限检测器实例
  firewallDetector: FirewallDetector;
  public isUsingVideoMixerInternalTrack = false;
  private _pendingHeartbeats: any[] = [];
  private _whipRebuildParams?: {
    roomId: string;
    useStringRoomId: boolean;
    relayInnerIp: string;
    relayOuterIp: string;
    relayPort: number;
  };
  public videoMixerOutputTrack: MediaStreamTrack | null = null;
  constructor(options: RoomOption) {
    super(options);
    // init properties

    // 初始化防火墙检测器
    this.firewallDetector = new FirewallDetector();
    this.firewallDetector.on(FirewallDetectorEvent.FIREWALL_RESTRICTION, () => {
      this._log.warn('firewall restriction');
      this.emit(RoomEvent.FIREWALL_RESTRICTION);
    });

    this._stats = new Stats(this, this._log);
    this.userManager = new UserManager(this.userId, this._log);
    // hidden parameter for special customers to use string roomId
    this._version = version;

    this.sdpSemantics = SDP_SEMANTICS_PLAN_B;
    if (!isUndefined(options.sdpSemantics)) {
      this.sdpSemantics = options.sdpSemantics as string;
    } else if (rtcDetector.isUnifiedPlanDefault()) {
      this.sdpSemantics = SDP_SEMANTICS_UNIFIED_PLAN;
    }
    this._log.info(`sdpSemantics: ${this.sdpSemantics}, netType: ${getNetworkType()}`);
    if (options.iceTransportPolicy) {
      this._iceTransportPolicy = options.iceTransportPolicy;
    }
    // 预留隐藏参数，支持开启一个房间推多个辅流
    this._enableMultiAuxStream = isUndefined(options.enableMultiAuxStream) ? false : options.enableMultiAuxStream as boolean;

    this.enableSEI = options.enableSEI && IS_SPC_SUPPORTED; // 只在 单 PC 支持 SEI
    if (!isUndefined(options.enableSPC) && IS_SPC_SUPPORTED) {
      this.enableSPC = options.enableSPC;
    }
    // @ts-ignore
    this.preferHW = !!options.preferHW;
    this.enableVolumeControlInIOS = options.enableVolumeControlInIOS;
    this._initBusinessInfo(options);

    this.healthDetector = new HealthDetector(this);
  }

  /** 当前用户是否有推主流 */
  get isMainStreamPublished() {
    return !!this.uplinkConnection?.isMainStreamPublished;
  }

  /** 当前用户是否有推音频 */
  get isMainAudioPublished() {
    return !!this.uplinkConnection?.localMainAudioTrack;
  }

  /** 当前用户是否有推辅流 */
  get isAuxStreamPublished() {
    return !!this.uplinkConnection?.isAuxStreamPublished;
  }

  /** 当前房间是否存在辅流，即判断本人 or 其他人是否有推辅流 */
  get hasAuxStream() {
    return [...this.remotePublishedUserMap.values()].findIndex(connection => connection.muteState.hasAuxiliary) >= 0;
  }

  get userMap() {
    return this.userManager.userMap;
  }

  get remotePublishedUserMap() {
    return this.userManager.remotePublishedUserMap;
  }

  // tinyId -> userId 的 map。由于 remotePublishedUserMap 的 key 是 userId，为降低时间复杂度，
  // 此 map 用于在仅有 tinyId 信息时，寻找 remotePublishedUser（即 DownlinkConnection）对象。
  get tinyIdToUserIdMap() {
    return new Map([...this.userMap.values()].map(item => [item.tinyId, item.userId]));
  }

  get videoCodec() {
    return this.singlePC?.videoCodec || VideoCodec.H264;
  }

  get downlinkVideoCodec() {
    return this.singlePC?.downlinkVideoCodec || VideoCodec.H264;
  }
  isEnableDtx() {
    // enableDtx 取决于 scheduleResult 的值:  0||undefined：未配置，返回this._enableDtx，1：强制开启，返回true，2 ：强制关闭 返回false, 其他情况返回false
    const scheduleResult = this.scheduleResult.config?.dtx;
    if (scheduleResult === 0 || scheduleResult === undefined) {
      return this._enableDtx;
    }
    if (scheduleResult === 1) {
      return true;
    }
    if (scheduleResult === 2) {
      return false;
    }
    return this._enableDtx;
  }
  @ChangeState([RoomState.LEFT, FSM.INIT], RoomState.JOINED)
  @addPromiseRetry({
    settings: {
      retries: 1,
      timeout: 0
    },
    onRetrying(this: Room, retryCount) {
      this._log.warn(`join retry ${retryCount}`);
    },
    onRetryFailed(this: Room, error: any) {
      this._log.error('join failed', error);
    },
    onError(this: Room, error, retry) {
      // 使用缓存调度进房失败，则开启调度，重试一次进房。
      if (this._isUsingCachedSchedule && !this.isDestroyed) {
        this._log.warn('is using cached schedule, retry join');
        setIsNeedToSchedule(true);
        this.reset();
        retry();
      } else if (this.signalChannel && this.signalChannel.isConnected && this.signalChannel.keepAlive) {
        // keepAlive 进房失败，重试一次。
        this._log.warn('is using keepAlive ws, retry join');
        this.signalChannel.close();
        this.reset();
        retry();
      } else {
        this.reset();
        retry();
      }
    }
  })
  @beforeJoin()
  async join(params: TRTCParams, scene: Scene, frameWorkType: number): Promise<void> {
    this.userManager.mySelfId = this.userId;
    // 监听 user 相关事件
    this.userManager.on(UserManagerEvent.PEER_JOIN, user => {
      this.emit(RoomEvent.PEER_JOIN, user);
    });
    this.userManager.on(UserManagerEvent.ASR_ROBOT_PEER_JOIN, user => {
      this.emit(RoomEvent.ASR_ROBOT_PEER_JOIN, user);
    });
    this.userManager.on(UserManagerEvent.ASR_ROBOT_PEER_LEAVE, user => {
      this.emit(RoomEvent.ASR_ROBOT_PEER_LEAVE, user);
    });
    this.userManager.on(UserManagerEvent.PEER_LEAVE, ({ userId, reason }) => {
      this.closeDownLinkConnection(userId, CONNECTION_CLOSED_REASON.REMOTE_LEAVE);
      this.emit(RoomEvent.PEER_LEAVE, { userId, reason });
    });
    this.userManager.on(UserManagerEvent.REMOTE_PUBLISHED, this.createDownlinkConnection, this);
    this.userManager.on(UserManagerEvent.REMOTE_UNPUBLISHED, this.closeDownLinkConnection, this);
    this.userManager.on(UserManagerEvent.REMOTE_PUBLISH_STATE_CHANGED, ({ ...args }) => {
      innerEmitter.emit(INNER_EVENT.REMOTE_PUBLISH_STATE_CHANGED, { room: this, ...args });
      this.emit(RoomEvent.REMOTE_PUBLISH_STATE_CHANGED, { ...args });
    });
    this.joinParams = params;
    if (isBoolean(params.enableDataChannel)) {
      this._enableDataChannel = params.enableDataChannel;
    }
    if (isBoolean(params.enableDtx)) {
      this._enableDtx = params.enableDtx;
    }
    return new Promise(async (resolve, reject) => {
      this._joinReject = reject;
      let isWhipFallback = false;
      try {
        this.checkDestroy();
        // whip 拉流
        if (this.whipQuickStartConfig) {
          const result = await this.tryWhipPull(params);
          if (result) return resolve();
          isWhipFallback = true;
        }
        try {
          await Promise.all([this.initialize(), this.initSinglePC()]);
        } catch (error) {
          if (error instanceof RtcError && error.code === ErrorCode.SPC_INITIALIZED_FAILED) {
            this.signalChannel?.destroy();
            // 单 PC 初始化失败，重新连接信令通道，走非竞速模式。ps: 竞速模式支持单 PC，非竞速模式支持多 PC
            await this.initialize();
          } else {
            if (isWhipFallback) {
              kvStatManager.addEnum({ key: KV_REPORT_KEY_WHIP.WHIP_RESULT, value: 2 });
            }
            return reject(error);
          }
        }
        const now = performanceNow();
        await this.doJoin(params, this.singlePC?.clientAbility);
        kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_NETWORK.JOIN_SEND_CMD, cost: performanceNow() - now });
        if (isWhipFallback) {
          kvStatManager.addEnum({ key: KV_REPORT_KEY_WHIP.WHIP_RESULT, value: 1 });
        }
        resolve();

        // 进房成功后 更新 publishedUserList
        // 以下代码无用，会被 isJoined 直接 return
        if (this._firstPublishedList) {
          this.onPublishedUserList({ data: { userList: this._firstPublishedList } });
        }
      } catch (error) {
        kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.JOIN_SEND_CMD, error: error as RtcError });
        if (isWhipFallback) {
          kvStatManager.addEnum({ key: KV_REPORT_KEY_WHIP.WHIP_RESULT, value: 2 });
        }
        reject(error);
      }
      this._joinReject = null;
    });
  }

  async initSinglePC(initializeOptions?: Parameters<SinglePeerConnection['initialize']>[0]) {
    if (!this.enableSPC || this.singlePC) return;
    this.singlePC = new SinglePeerConnection({
      signalChannel: this.signalChannel ?? undefined,
      room: this,
      enableDataChannel: this._enableDataChannel,
      enableDtx: this.isEnableDtx()
    });
    this.singlePC.on(SPC_EVENT.SEI_MESSAGE, event => this.emit(RoomEvent.SEI_MESSAGE, event));
    this.singlePC.on(SPC_EVENT.DUMP, event => this.emit(RoomEvent.DUMP, event));
    this.singlePC.once(SPC_EVENT.ERROR, () => this.fallbackToMPC());
    this.singlePC.on(SPC_EVENT.DATA_CHANNEL_MESSAGE, event => {
      const data = (new TextDecoder()).decode(event.data.data || event.data);
      try {
        this.emit(RoomEvent.DATA_CHANNEL_MESSAGE, { data: JSON.parse(data) });
      } catch (e) {
        // ignore non-JSON data
      }
    });
    try {
      return await this.singlePC.initialize(initializeOptions);
    } catch (error: any) {
      this.fallbackToMPC();
      throw new RtcError({ code: ErrorCode.SPC_INITIALIZED_FAILED, message: error?.message });
    }
  }

  private buildJoinData(params: TRTCParams, clientAbility?: ClientAbility) {
    // 优先级 jitterBuffer > playoutDelay（iOS 等不支持 jitterBuffer 的设备除外）
    // aux 不支持 playoutDelay，使用 jitterBufferDelay 设置
    const hasJitterBuffSchedule = this.scheduleResult?.config?.jitterDelay || this.scheduleResult?.config?.jitterDelayAux;
    if (!hasJitterBuffSchedule || !IS_JITTER_BUFFER_TARGET_SUPPORTED) {
      if (this.scene === Scene.LIVE && this.role === UserRole.AUDIENCE && !this.jitterBufferDelay && !this.playoutDelay) {
        const DEFAULT_AUDIENCE_DELAY = this.latencyLevel === LatencyLevel.NORMAL ? 1000 : 700;
        this._log.debug('set default audience playoutDelay', DEFAULT_AUDIENCE_DELAY);
        this.playoutDelay = { min: DEFAULT_AUDIENCE_DELAY, max: 2500 };
        this.jitterBufferDelay = { aux: DEFAULT_AUDIENCE_DELAY };
      }
      if (clientAbility && this.playoutDelay) {
        this._log.info('set playoutDelay', JSON.stringify(this.playoutDelay));
        clientAbility.playoutDelay = this.playoutDelay;
      }
    }
    return {
      roomId: String(params.roomId || params.strRoomId),
      useStringRoomId: this.useStringRoomId,
      privateMapKey: this.privateMapKey,
      latencyLevel: this.latencyLevel,
      trtcRole: params.role,
      trtcScene: this.scene === Scene.LIVE ? SceneNumber.LIVE : SceneNumber.RTC,
      sdpSemantics: this.sdpSemantics,
      version: innerVersion,
      ua: (navigator && navigator.userAgent) || '',
      terminalType: getTerminalType(),
      netType: getNumNetworkType(),
      // 后端参数拼写错误
      bussinessInfo: this._businessInfo,
      ability: clientAbility,
      sdkType: this._sdkType,
      userSig: this.userSig,
      receiveMix: true,
      isChorus: !!this.enableChorus,
      enableNtpAudioFrame: !!this.enableChorus && isWebCodecsSupported(),
      transcription: this._enableDataChannel,
      downUseVp8: this.scheduleResult.config?.downUseVp8 || false
    };
  }

  doJoin(options: TRTCParams, clientAbility?: ClientAbility) {
    return new Promise<void>(async (resolve, reject) => {
      if (options.privateMapKey) {
        this.privateMapKey = options.privateMapKey;
      }
      if (options.latencyLevel) {
        this.latencyLevel = options.latencyLevel;
      }

      // signal channel Error handler
      this.signalChannel!.once(ChannelEvent.SETUP_FAILED, (error: any) => {
        this.clearJoinTimeout();
        innerEmitter.emit(INNER_EVENT.JOIN_SIGNAL_CONNECTION_END, { room: this, error });
        reject(error);
      });

      if (isBoolean(this.scheduleResult?.config?.singlePC) && IS_SPC_SUPPORTED) {
        this.enableSPC = this.scheduleResult.config!.singlePC;
      }

      this.keyPointManager.setConnectionType(this.singlePC ? KeyPointConnectionType.SINGLE_PC : KeyPointConnectionType.MULTI_PC);

      // join room parameters
      const data = this.buildJoinData(options, clientAbility);

      this._log.debug(`join room signal data: ${JSON.stringify(data)}`);

      // schedule timeout to check if join room could be finished in 2s
      let JOIN_TIMEOUT = 5000;
      // 云端调控进房超时配置，最低 1s
      if (this.scheduleResult.config?.enterRoomTimeout && this.scheduleResult.config.enterRoomTimeout >= 1) {
        JOIN_TIMEOUT = this.scheduleResult.config.enterRoomTimeout * 1000;
      }
      this._joinTimeout = window.setTimeout(() => {
        reject(new RtcError({
          code: ErrorCode.JOIN_ROOM_FAILED,
          message: generateErrorMessage({ key: ErrorName.JOIN_ROOM_TIMEOUT })
        }));
      }, JOIN_TIMEOUT);
      innerEmitter.emit(INNER_EVENT.JOIN_SEND_CMD, { room: this });

      this.signalChannel!.send(this.singlePC ? SignalCommand.SPC_JOIN_ROOM : SignalCommand.JOIN_ROOM, data);
      this.signalChannel!.once(SignalEvent.JOIN_ROOM_RESULT, async (evt: any) => {
        this.clearJoinTimeout();
        const { code, message, data, tinyId } = evt.data;
        innerEmitter.emit(INNER_EVENT.JOIN_RECEIVED_CMD_RES, { room: this, code });
        if (code === 0) {
          this._log.info('Join room success, start heartbeat');
          if (tinyId) {
            this.tinyId = tinyId;
          }
          this.startHeartbeat();
          this.syncUserList();
          this.startSyncUserListInterval();
          this._firstPublishedList = data.publishers;
          this._iceServersFromJoin = data.iceServer ? [data.iceServer] : [];
          if (this.singlePC) {
            this.singlePC.setIceServers(this.getIceServers())
              .then(() => {
                this.singlePC?.connect({
                  ...data.ability,
                  useVp8: data.ability.useVp8 || !!options.useVp8,
                  useH265: data.ability.useH265 && !!options.useH265
                }).catch(() => { });
              });
          }
          resolve();
        } else {
          this._log.error(`Join room failed result: ${code} error: ${message}`);
          reject(new RtcError({
            code: ErrorCode.JOIN_ROOM_FAILED,
            extraCode: code,
            message: generateErrorMessage({
              key: ErrorName.JOIN_ROOM_FAILED,
              data: { error: message, code }
            })
          }));
        }
      });
    });
  }

  /**
   * 重进房
   * 调用时机：ws 重连成功后，SDK 需要重进房并且重新推流
   * 恢复上行：重进房后，如果还有上行 peerConnection，则需要重新推流，重建上行 peerConnection
   * 恢复下行：重进房后，后台会下发 stream-added，SDK 根据 stream-added 来重建下行连接，并自动恢复订阅状态
   * @private
   * @memberof Client
   */
  async reJoin() {
    // 用户未进房，不需要重进房
    if (!this.isJoined) {
      this._log.warn('reJoin abort');
      return;
    }
    try {
      this._log.warn(`reJoin pending: ${this.joinParams!.roomId}`);
      let clientAbility;
      const promiseList = [];
      if (this.singlePC) {
        this.singlePC.close();
        this.singlePC = null;
        promiseList.push(this.initSinglePC().then(result => {
          clientAbility = result;
          return result;
        }));
      }
      if (this.signalChannel) {
        this.signalChannel.close();
        promiseList.push(this.signalChannel.connect());
      }
      await Promise.all(promiseList);
      await this.doJoin({
        ...this.joinParams!,
        role: this.role === UserRole.ANCHOR ? UserRoleNumber.ANCHOR : UserRoleNumber.AUDIENCE,
        privateMapKey: this.privateMapKey,
        latencyLevel: this.latencyLevel
      }, clientAbility);
      this._log.warn('reJoin success');
      eventLogger.logSuccessEvent({ userId: this.userId, eventType: KIBANA_EVENT.REJOIN });

      if (this.singlePC) {
        const onConnected = (event: { state: ConnectionState; }) => {
          if (event.state === ConnectionState.CONNECTED) {
            this.singlePC?.off(SPC_EVENT.CONNECTION_STATE_CHANGED, onConnected);
            if (this.uplinkConnection instanceof UplinkConnectionSPC) {
              this.uplinkConnection.installEvents();
              this.uplinkConnection.onSinglePCReconnected();
            }
            this.remotePublishedUserMap.forEach(downlinkConnection => {
              downlinkConnection.installEvents();
              (downlinkConnection as DownlinkConnectionSPC).onSinglePCReconnected();
            });
          }
        };
        (this.singlePC as SinglePeerConnection).on(SPC_EVENT.CONNECTION_STATE_CHANGED, onConnected);
        this.checkConnectionsToReconnect();
        if (this.uplinkConnection instanceof UplinkConnection && !this.uplinkConnection.getIsReconnecting()) {
          // 重进房后，后台会重置状态机，会停止发送 STUN 回包，
          // 如果此时有上行，则需要重新推流。否则上行会因为收不到 STUN 包而断开
          // 当有上行且未在重连时，主动重连上行
          (this.uplinkConnection as UplinkConnection).startReconnection();
        }
      }
    } catch (error) {
      this._log.warn(`reJoin fail ${error}`);
      this.reset();
      eventLogger.logFailedEvent({ userId: this.userId, eventType: KIBANA_EVENT.REJOIN, error });
      this.emit(
        RoomEvent.ERROR,
        new RtcError({
          code: ErrorCode.JOIN_ROOM_FAILED,
          message: generateErrorMessage({
            key: ErrorName.REJOIN_ROOM_FAILED,
            data: { roomId: this.joinParams!.roomId }
          })
        })
      );
    }
  }

  /**
   * setup the signal channel.
   *
   * @private
   * @param options.preconnect
   * @returns {Promise}
   * @memberof Client
   */
  async initialize(options?: { isPreconnect?: boolean }) {
    // 非预连接进房，如果有未完成的预连接，先等待
    if (!options?.isPreconnect && this.preconnectPromise) await this.preconnectPromise.catch(() => {});
    // 有预连接，且预连接有效，则复用预连接，否则关闭无效的 ws
    if (this.signalChannel?.hasPreconnect) {
      if (this.signalChannel.isPreconnectValid(this.sdkAppId, this.userId, this.userSig)) {
        this._log.info('reuse preconnect signal channel');
        this.signalChannel.consumePreconnect();
        return;
      }
      this.signalChannel.close();
    }

    const { mainUrl, backupUrl } = this.getSignalChannelUrl();
    const prevKeepAliveSignalChannel = this.signalChannel || getActiveKeepAliveSignalChannel(this.userId);
    const isReuseSignalChannel = !this._whipRebuildParams && !!(prevKeepAliveSignalChannel && prevKeepAliveSignalChannel.isConnected && prevKeepAliveSignalChannel.keepAlive && prevKeepAliveSignalChannel.userId === this.userId);
    let domain;
    if (Array.isArray(this.scheduleResult.domains) && this.scheduleResult.domains.length > 0) {
      domain = this.scheduleResult.domains[0];
    }
    this._log.info(`${isReuseSignalChannel ? 'reuse' : 'setup'} signal channel`);
    if (isReuseSignalChannel) {
      // keepAlive 的 ws 在重新进房时，可能调度拿到的域名不一样，此时更新 ws 域名，后续若 ws 重连则重连新的域名。
      prevKeepAliveSignalChannel.url = mainUrl;
      prevKeepAliveSignalChannel.backupUrl = backupUrl;
      prevKeepAliveSignalChannel.room.setSignalChannel(null);
      prevKeepAliveSignalChannel.room = this;
      this.signalChannel = prevKeepAliveSignalChannel;
    } else {
      if (prevKeepAliveSignalChannel) prevKeepAliveSignalChannel.close();
      this.signalChannel = new SignalChannel({
        sdkAppId: this.sdkAppId,
        userId: this.userId,
        userSig: this.userSig,
        url: mainUrl,
        backupUrl,
        room: this,
        signalDomainWhenUnifiedProxy: this.proxy_unified ? domain : undefined,
        preconnect: options?.isPreconnect,
        whipRebuildParams: this._whipRebuildParams
      });
      this._customMessageManager = new CustomMessageManager(this, this.signalChannel);
      this._customMessageManager.on(CustomMessageManagerEvent.MESSAGE, data => {
        this.emit(RoomEvent.CUSTOM_MESSAGE, data);
      });
    }
    if (!this.networkQuality) {
      this.networkQuality = new NetworkQuality({
        signalChannel: this.signalChannel,
        room: this
      });
      this.networkQuality.on(NetworkQuality.EVENT_NETWORK_QUALITY, (event: NetworkQuality) => {
        this.emit(RoomEvent.NETWORK_QUALITY, event);
        this.singlePC?.detectTCPAndUDP(event);
      });
    }

    eventManager.create(this, this.signalChannel)
      .add(ChannelEvent.CONNECTION_STATE_CHANGED, (event: any) => {
        innerEmitter.emit(INNER_EVENT.SIGNAL_CONNECTION_STATE_CHANGED, { room: this, ...event });
        this.emit(RoomEvent.SIGNAL_CONNECTION_STATE_CHANGED, event);
      })
      .add(ChannelEvent.RECONNECT_FAILED, (error: any) => {
        // WS 重连失败，重置当前 client，接入侧可以调用进房接口来重新进房。
        this.reset();
        this.emit(RoomEvent.ERROR, error);
      })
      .add(SignalEvent.PEER_JOIN, (event: any) => {
        const { srcTinyId: tinyId, userId, role, fromType } = event.data.data;
        this.userManager.addUser({ userId, tinyId, role, fromType });
      })
      .add(SignalEvent.PEER_LEAVE, (event: any) => {
        const { userId, reason = 0 } = event.data.data;
        this.userManager.deleteUser(userId, reason);
      })
      .add(SignalEvent.UPDATE_REMOTE_MUTE_STAT, (event: any) => {
        // 不推拉流的情况下，长时间页面切后台，定时器会限频。此处根据 cmd 23 来补心跳包保活。
        // ref: http://tapd.oa.com/TerWebG/bugtrace/bugs/view?bug_id=1020396022097757549
        if (this._lastHeartBeatTime > 0 && Date.now() - this._lastHeartBeatTime >= 10 * 1000) {
          this.doHeartbeat();
        }
        this.onPublishedUserList(event.data);
      })
      .add(SignalEvent.CLIENT_BANNED, (event: any) => {
        const content = event.data.data;
        // reason 前后端映射
        // kick 同userID互踢下线 WebRTC 环境独有
        // banned 管理员踢人
        // room_disband 管理员解散房间（20220714 增加）抛出 room-disband
        // user_time_out 用户超时，后台长时间没收到心跳，不抛出。
        const { reason } = content;
        eventLogger.uploadEvent({ log: `stat-banned:${reason}`, userId: this.userId });
        if (reason === BannedReason.USER_TIME_OUT) {
          // 打印上一次发送心跳的时间。
          // 最近遇到比较多的 user_time_out 的 case，后台排查结论是超过30s没收到 SDK 的2s心跳导致。
          // 需要在此加该日志辅助问题排查，确认出现 user_time_out 时，上一次发送心跳的时间是否超过 30s
          this._log.warn(`${reason} last heart beat time: ${this._lastHeartBeatTime} interval: ${Date.now() -
            this._lastHeartBeatTime}, visibility: ${document.visibilityState}`);
          // 出现 user_time_out 时，sdk 做重进房进行恢复。
          // 断连重连30s刚好重连上，可能会出现 user_time_out 被踢。
          this.reJoin();
          return;
        }
        // kibana 统计被踢分布
        this._log[reason === 'kick' ? 'error' : 'info'](`user was banned because of [${reason}]`);
        // 被踢时，调用该方法，及时清理内部状态
        // ref: http://tapd.oa.com/TerWebG/bugtrace/bugs/view/1020396022083250157
        this.reset();
        // 2022-7-19 改为抛出事件数据对象，默认值都为后台 reason。
        this.emit(RoomEvent.BANNED, {
          reason
        });
      })
      .add(SignalEvent.SEND_SWITCH_ROOM_SUBED_REQ, (event: any) => {
        // cmd 4161 下发时机: 1. 切换房间成功后立刻发一次 2. 信令携带的 ssrcGroups 多于切换房间时的已推流用户数, 后续有新的用户进房推流
        if (!this.singlePC) return;
        const { subList } = event.data.data;
        this._log.info(`auto subscribe ${stringify(subList, { keysToInclude: ['userId'] })}`);
        subList.forEach((user: { srcTinyId: string, userId: string, groupIndex: number }) => {
          this.singlePC!.autoSubscribedUserMap.set(user.userId, user);
        });
        this.resolveSwitchRoomSubedReq();
      })
      // 监听后台下发的回退编解码器事件，265的流被订阅时，如果订阅者不支持265，则会触发该事件
      .add(SignalEvent.FALLBACK_CODEC, async (event: any) => {
        const content = event.data.data;
        if (content.videoControlInfo?.enableH265Enc === 0 && this.singlePC?.videoCodec === VideoCodec.H265) {
          this._log.warn(`fallback codec enableH265Enc: ${content.videoControlInfo?.enableH265Enc}`);
          kvStatManager.addCount({
            key: KV_REPORT_KEY_VIDEO_ENCODE.ENCODE_H265_FALLBACK
          });
          await this.singlePC?.switchVideoEncoder(VideoCodec.H264);
          await this.uplinkConnection?.sendMediaSettings();
        }
        // 下行支持H265无缝切换H264，暂时不需要手动切换
      });

    // signal channel setup success handler
    this.signalChannel.once(ChannelEvent.SETUP_SUCCESS, (event: any) => {
      this.tinyId = event.signalInfo.tinyId;
      innerEmitter.emit(INNER_EVENT.JOIN_SIGNAL_CONNECTION_END, { room: this });
    });

    innerEmitter.emit(INNER_EVENT.JOIN_SIGNAL_CONNECTION_START, {
      room: this
    });
    await this.signalChannel.connect();
    if (isReuseSignalChannel) {
      innerEmitter.emit(INNER_EVENT.JOIN_SIGNAL_CONNECTION_END, { room: this });
    }
    return isReuseSignalChannel;
  }

  setSignalChannel(signalChannel: SignalChannel | null) {
    this.signalChannel = signalChannel;
    if (!signalChannel) {
      // 设置为 null 时解绑时间
      eventManager.remove(this);
    }
  }

  /**
   * 退出当前音视频通话房间，结束一次音视频通话会话。<br>
   * 退房前请确保已经通过 {@link Client#unpublish unpublish()} 取消发布本地流，若未取消发布本地流，SDK 内部会自动取消发布本地流。
   * 同时，退房会关闭所有远端流。
   *
   * @returns {Promise}
   * @memberof Client
   * @example
   * client.leave().then(() => {
   *   // leaving room success
   * }).catch(error => {
   *   console.error('leaving room failed: ' + error);
   * });
   */
  @ChangeState(RoomState.JOINED, RoomState.LEFT, {
    ignoreError: true, success(this: Room) {
      this.reset(true);
    }
  })
  @leaveInnerEvent()
  @clearCallQueue('leave room')
  @removePromiseRetry({ fnName: 'publish', validateArgs: false })
  @removePromiseRetry({ fnName: 'unsubscribe', validateArgs: false })
  async leave() {
    try {
      // ensure all of events are uploaded
      await this.doHeartbeat();
    } catch (error) {
      // ignore
    }
    this._log.info('leave() => leaving room');
    innerEmitter.emit(INNER_EVENT.LEAVE_SEND_CMD, { room: this });
    this.signalChannel?.send(SignalCommand.LEAVE_ROOM);

    this.cleanupWhipResources();

    this.switchRoomSubedReq = undefined;
    this._changeBigSmallRecords.clear();
  }

  private async tryWhipPull(params: TRTCParams): Promise<boolean> {
    try {
      await this.doWhipPull(params);
      kvStatManager.addEnum({ key: KV_REPORT_KEY_WHIP.WHIP_RESULT, value: 0 });
      delete this.whipQuickStartConfig;
      return true;
    } catch (whipError: any) {
      this._log.warn('quickStart failed, fallback to normal join', whipError);
      delete this.whipQuickStartConfig;
      if (this.singlePC) {
        this.singlePC.close();
        this.singlePC = null;
      }
      this.userManager.clear();
      this.cleanupWhipResources();
      return false;
    }
  }

  private async doWhipPull(params: TRTCParams) {
    if (!this.whipQuickStartConfig) return;
    if (!IS_SPC_SUPPORTED) throw new RtcError({ code: ErrorCode.NOT_SUPPORTED, message: 'SPC is required for QuickStart, but not supported' });
    const startTime = performanceNow();

    if (params.privateMapKey) {
      this.privateMapKey = params.privateMapKey;
    }
    if (params.latencyLevel) {
      this.latencyLevel = params.latencyLevel;
    }

    // 传了 domain 时直接走 WHIP，不需要调度和云控校验
    if (!this.whipQuickStartConfig.domain) {
      if (!this.scheduleResult.domains || !this.scheduleResult.config) {
        await this.schedule(params);
        this._log.info(`whip scheduled-domains: ${JSON.stringify(this.scheduleResult.domains)}`);
      }
      // 检查 quickStart 云控，如果没有云控或云控为 false，直接回退正常进房
      if (this.scheduleResult.config?.quickStart !== true) {
        throw new RtcError({ code: ErrorCode.INVALID_OPERATION, message: 'quickStart is not enabled, use normal join' });
      }
    }

    const clientAbility = await this.initSinglePC();
    if (!this.singlePC) throw new RtcError({ code: ErrorCode.NOT_SUPPORTED, message: 'SinglePC is not initialized' });

    const joinData = this.buildJoinData(params, clientAbility);

    const subscribeSmall = this.whipQuickStartConfig.small === true;

    const subscribeData = {
      srcUserId: this.whipQuickStartConfig.remoteUserId,
      srcTinyId: '',
      audio: true,
      bigVideo: !subscribeSmall,
      auxVideo: false,
      smallVideo: subscribeSmall,
      customData: false,
      ssrc: buildSSRCGroup()
    };

    const requestBody: WhipPullRequestBody = {
      join: {
        cmd: SignalCommand.SPC_JOIN_ROOM,
        data: joinData,
        userId: params.userId
      },
      subscribe: {
        cmd: SignalCommand.SPC_SUBSCRIBE,
        data: subscribeData,
        userId: params.userId
      }
    };

    const whipRequestStart = performanceNow();
    let result: WhipPullResult;
    try {
      result = await this.sendWhipRequest({
        sdkAppId: params.sdkAppId,
        userId: params.userId,
        userSig: params.userSig,
        targetUserId: this.whipQuickStartConfig.remoteUserId
      }, requestBody);
      this._log.debug(`quickStart result: ${JSON.stringify(result)}`);
      const whipRequestCost = performanceNow() - whipRequestStart;
      kvStatManager.addSuccessEvent({
        key: KV_REPORT_KEY_NETWORK.WHIP_REQUEST,
        cost: whipRequestCost
      });
      innerEmitter.emit(INNER_EVENT.WHIP_REQUEST_COST, { room: this, cost: Math.round(whipRequestCost) });
      innerEmitter.emit(INNER_EVENT.WHIP_PULL_RESULT, { room: this, code: 0 });
    } catch (error: any) {
      kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.WHIP_REQUEST, error });
      kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.WHIP_TOTAL, error });
      const errorCode = error instanceof RtcError
        ? Number(error.getExtraCode() || error.getCode()) || ErrorCode.UNKNOWN
        : ErrorCode.UNKNOWN;
      innerEmitter.emit(INNER_EVENT.WHIP_PULL_RESULT, { room: this, code: errorCode });
      throw error;
    }
    if (result.tinyId) {
      this.tinyId = result.tinyId;
    }

    this._iceServersFromJoin = result.iceServer
      ? (Array.isArray(result.iceServer) ? result.iceServer : [result.iceServer])
      : [];

    // whip 拉流结果未返回 tinyId，需在 WS rebuild 后通过 cmd 4137 (USER_LIST_RES) 更新为远端用户真实的 tinyId。
    const whipRemoteTinyIdPlaceholder = `whip_${this.whipQuickStartConfig.remoteUserId}`;
    this.userManager.addUser({
      userId: this.whipQuickStartConfig.remoteUserId,
      tinyId: whipRemoteTinyIdPlaceholder,
      role: UserRoleNumber.ANCHOR
    });
    this.createDownlinkConnection({ userId: this.whipQuickStartConfig.remoteUserId, tinyId: whipRemoteTinyIdPlaceholder, flag: 0 });

    const pcStartTime = performanceNow();
    try {
      if (result.iceServer) {
        const iceServers = Array.isArray(result.iceServer) ? result.iceServer : [result.iceServer];
        await this.singlePC.setIceServers(iceServers);
      }
      await this.singlePC.connect(result.ability, false);
      kvStatManager.addSuccessEvent({
        key: KV_REPORT_KEY_NETWORK.WHIP_PC_CONNECT,
        cost: performanceNow() - pcStartTime
      });
    } catch (error: any) {
      kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.WHIP_PC_CONNECT, error });
      kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.WHIP_TOTAL, error });
      throw error;
    }

    const downlinkConnection = this.remotePublishedUserMap.get(this.whipQuickStartConfig.remoteUserId) as DownlinkConnectionSPC;
    if (downlinkConnection) {
      const { audioSsrc, bigVideoSsrc, bigVideoRtxSsrc, auxVideoSsrc, auxVideoRtxSsrc } = subscribeData.ssrc;
      downlinkConnection.ssrc = {
        audio: audioSsrc,
        video: bigVideoSsrc,
        videoRtx: bigVideoRtxSsrc,
        auxiliary: auxVideoSsrc,
        auxiliaryRtx: auxVideoRtxSsrc
      };
      await this.singlePC.addDownlink({
        userId: this.whipQuickStartConfig.remoteUserId,
        tinyId: whipRemoteTinyIdPlaceholder,
        ssrc: downlinkConnection.ssrc
      });
    }

    this.handleWhipTracks(this.whipQuickStartConfig);

    this.startHeartbeat();

    innerEmitter.emit(INNER_EVENT.JOIN_SIGNAL_CONNECTION_START, { room: this });
    const wsRebuildStartTime = performanceNow();
    try {
      await this.rebuildSignalChannel(params, result);
      kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_NETWORK.WHIP_WS_REBUILD, cost: performanceNow() - wsRebuildStartTime });
    } catch (error: any) {
      this._log.error(error);
      kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.WHIP_WS_REBUILD, error });
    }

    kvStatManager.addSuccessEvent({
      key: KV_REPORT_KEY_NETWORK.WHIP_TOTAL,
      cost: performanceNow() - startTime
    });
  }

  private handleWhipTracks(whip: NonNullable<typeof this.whipQuickStartConfig>) {
    const userId = whip.remoteUserId;

    const publishedUser = this.remotePublishedUserMap.get(userId);
    if (!publishedUser) {
      return;
    }

    const hasAudio = !!publishedUser.remoteAudioTrack.mediaTrack;
    const hasVideo = !!publishedUser.remoteVideoTrack.mediaTrack;

    let flag = 0;
    if (hasVideo) flag |= 0x01; // VIDEO_STAT_BIT
    if (hasAudio) flag |= 0x08; // AUDIO_STAT_BIT
    (publishedUser as DownlinkConnectionSPC).flag = flag;
    innerEmitter.emit(INNER_EVENT.RECEIVED_PUBLISHED_USER_LIST, {
      room: this,
      publishedUserList: [{ userId, tinyId: userId, flag }],
      isWhipPreFill: true
    });

    const streamType = RemoteStreamType.Main;

    const prevMuteState = getMuteStateFromFlag(0, userId);
    const muteState = getMuteStateFromFlag(flag, userId);
    innerEmitter.emit(INNER_EVENT.REMOTE_PUBLISH_STATE_CHANGED, {
      room: this,
      prevMuteState,
      muteState
    });
    this.emit(RoomEvent.REMOTE_PUBLISH_STATE_CHANGED, { prevMuteState, muteState, flag });

    innerEmitter.emit(INNER_EVENT.SUBSCRIBE_START, {
      room: this,
      streamType,
      remotePublishedUser: publishedUser,
      subscribeState: {
        audio: hasAudio,
        video: hasVideo && !whip.small,
        smallVideo: hasVideo && !!whip.small,
        auxiliary: false,
        datachannel: false
      }
    });

    const subscribedPromise = Promise.resolve();
    if (hasVideo && !publishedUser.remoteVideoTrack.isSubscribed) {
      if (whip.small) {
        publishedUser.remoteVideoTrack.setMediaType(MediaType.SMALL_VIDEO);
      }
      publishedUser.remoteVideoTrack.subscribe(subscribedPromise);
    }
    if (hasAudio && !publishedUser.remoteAudioTrack.isSubscribed) {
      publishedUser.remoteAudioTrack.subscribe(subscribedPromise);
    }

    innerEmitter.emit(INNER_EVENT.SUBSCRIBE_SUCCESS, {
      room: this,
      streamType,
      remotePublishedUser: publishedUser
    });
  }

  @addPromiseRetry({
    settings: { retries: 1, timeout: 0 },
    onRetrying(this: Room, retryCount) {
      this._log.warn(`send sub request retry ${retryCount}`);
    },
    onError(this: Room, error, retry, reject) {
      this._log.warn(`send sub request failed. error: ${error.message}`);
      // 5004: 后台不支持该应用，无需重试，直接回退到正常进房流程
      if (error instanceof RtcError && error.extraCode === 5004) {
        reject(error);
        return;
      }
      retry();
    }
  })
  private async sendWhipRequest(
    options: WhipPullOptions,
    body: WhipPullRequestBody
  ): Promise<WhipPullResult> {
    const { sdkAppId, userId, userSig, targetUserId } = options;
    const params = new URLSearchParams({ sdkAppId: String(sdkAppId), userId, userSig });
    if (targetUserId) params.set('targetUserId', targetUserId);
    params.set('roomId', this.roomId);
    params.set('useStringRoomId', String(this.useStringRoomId));
    const domain = this.whipQuickStartConfig?.domain || this.scheduleResult.domains?.[0];
    if (!domain) {
      throw new RtcError({ code: ErrorCode.UNKNOWN, message: 'whip domain is not available' });
    }
    const url = `https://${domain}/v4/sub?${params}`;

    const WHIP_REQUEST_TIMEOUT = 5000;
    const startTime = performanceNow();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), WHIP_REQUEST_TIMEOUT);
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(body),
        signal: controller.signal
      });
      if (!response.ok) {
        throw new RtcError({
          code: ErrorCode.UNKNOWN,
          extraCode: response.status,
          message: `whip request failed with HTTP ${response.status} ${response.statusText}`
        });
      }
      const result = await response.json();

      const cost = Math.round(performanceNow() - startTime);
      this._log.info(`whip https success. cost: ${cost}`);

      if (result.code !== 0) {
        throw new RtcError({
          code: ErrorCode.UNKNOWN,
          extraCode: result.code,
          message: result.message
        });
      }

      return result.data;
    } catch (error: any) {
      const cost = Math.round(performanceNow() - startTime);
      this._log.warn(`whip https failed, cost: ${cost}ms, error: ${error.message}`);
      if (error instanceof RtcError) throw error;
      throw new RtcError({
        code: ErrorCode.UNKNOWN,
        message: error.message
      });
    } finally {
      clearTimeout(timer);
    }
  }

  private async rebuildSignalChannel(
    params: TRTCParams,
    result: WhipPullResult
  ) {
    this._whipRebuildParams = {
      roomId: String(params.roomId || params.strRoomId),
      useStringRoomId: Boolean(!params.roomId || params.strRoomId),
      relayInnerIp: result.relayInnerIp,
      relayOuterIp: result.relayOuterIp,
      relayPort: result.relayPort
    };

    await this.initialize();

    const signalInfo = this.signalChannel!.getSignalInfo();
    if (result.relayOuterIp) signalInfo.relayIp = result.relayOuterIp;
    if (result.relayInnerIp) signalInfo.relayInnerIp = result.relayInnerIp;
    if (result.relayPort) signalInfo.relayPort = result.relayPort;
    if (result.bakRelayIps) signalInfo.bakRelayIps = result.bakRelayIps;
    if (result.tinyId) signalInfo.tinyId = result.tinyId;
    if (result.endReportExtend) signalInfo.endReportExtend = result.endReportExtend;
    if (result.reportToken) signalInfo.reportToken = result.reportToken;

    this.flushPendingHeartbeats();
    this.syncUserList();
    this.startSyncUserListInterval();

    this._log.info('rebuild signal channel success');
  }

  private cleanupWhipResources() {
    this._pendingHeartbeats = [];
    delete this._whipRebuildParams;
  }

  private flushPendingHeartbeats() {
    if (this._pendingHeartbeats.length === 0) return;
    const pendingReports = this._pendingHeartbeats.splice(0);

    for (const report of pendingReports) {
      if (this.signalChannel?.isConnected) {
        this.signalChannel.send(SignalCommand.ON_QUALITY_REPORT, report);
      }
    }
  }

  clearNetworkQuality() {
    if (this.networkQuality) {
      this.networkQuality.stop();
      delete this.networkQuality;
    }
  }

  // close all downlink connections
  closeConnections() {
    this.remotePublishedUserMap.forEach(connection => {
      this.closeDownLinkConnection(connection.userId, CONNECTION_CLOSED_REASON.LOCAL_LEAVE);
    });
  }

  clearJoinTimeout() {
    clearTimeout(this._joinTimeout);
    this._joinTimeout = -1;
  }

  /**
   *
   * @private
   * @memberof Client
   */
  startHeartbeat() {
    if (this._heartbeat === -1) {
      this._heartbeat = timer.run(TIMER_TYPE.RIC, this.doHeartbeat.bind(this), { delay: 2000 });
      if (this.enableChorus) this.startUpdateNTPTime();
    }
  }

  /**
   *
   * @private
   * @memberof Client
   */
  stopHeartbeat() {
    if (this._heartbeat !== -1) {
      this._log.info('stopHeartbeat');
      timer.clearTask(this._heartbeat);
      this._heartbeat = -1;
      this._lastHeartBeatTime = -1;
    }
  }

  /**
   * 心跳上报，数据落地仪表盘、秒级上报
   * @private
   * @memberof Client
   */
  async doHeartbeat() {
    const freezeMap = this.badCaseDetector.getMonitorFreeze();

    const stats = await this._stats.getStatsReport({
      uplinkConnection: this.uplinkConnection,
      downlinkConnections: this.remotePublishedUserMap,
      freezeMap
    });

    // send event only when the signal channel is connected.
    const eventList = this.signalChannel?.isConnected ? getEvents(this.userId) : [];
    const report: HearBeatReport = {
      str_sdk_version: innerVersion,
      uint64_datetime: new Date().getTime(),
      msg_user_info: {
        str_identifier: this.userId,
        uint64_tinyid: this.tinyId!
      },
      msg_event_msg: eventList as TC_Event_Msg[],
      str_acc_ip: this.getSignalInfo().relayIp,
      str_client_ip: this.getSignalInfo().clientIp,
      ...stats,
      msg_device_info: {
        uint32_terminal_type: 15, // 终端类型, 固定 15
        str_device_name: getDeviceModel(), // 设备名字(型号)
        str_os_version: '', // 设备系统版本,目前没有设置
        uint32_net_type: getNumNetworkType(), // Net类型信息 1：wifi 2：4G 3：3G 4：2G 5:有线
        ...stats.msg_device_info
      }
    };
    this.heartbeatReport = report;
    this.heartbeatCount++;
    innerEmitter.emit(INNER_EVENT.HEARTBEAT_REPORT, { room: this, report });
    // report only when the signal channel is not empty
    if (this.signalChannel) {
      if (this.signalChannel.isConnected) {
        this.signalChannel.send(SignalCommand.ON_QUALITY_REPORT, report);
        const now = Date.now();
        if (this._lastHeartBeatTime > 0 && now - this._lastHeartBeatTime > 10000) {
          this._log.warn(`heartbeat took ${now - this._lastHeartBeatTime}`);
        }
        this._lastHeartBeatTime = now;

        if (!this.signalChannel.isOnline) {
          this._log.warn('signal channel is not online');
          this.signalChannel.startReconnection();
        }
      } else if (this.whipQuickStartConfig) {
        this._pendingHeartbeats.push(report);
      }
      this.emit(RoomEvent.HEARTBEAT_REPORT, {
        ...report,
        bytes_sent: this._stats.totalBytesSent + this.signalChannel.bytesSent,
        bytes_received: this._stats.totalBytesReceived + this.signalChannel.bytesReceived
      });
    } else if (this.whipQuickStartConfig) {
      // whip 拉流，在 ws rebuild 前暂存心跳数据
      this._pendingHeartbeats.push(report);
    }
    if (!this._isRelayChanged && this.isRelayMaybeFailed()) {
      // relay 服务器可能已宕机，走重进房流程更换 relayIp
      this.reJoin();
      // 只执行一次重进房即可，避免出现重复重进房。
      this._isRelayChanged = true;
    }

    this.badCaseDetector.resetMonitor();
  }

  /**
   * 收到后台发给 SDK 的【已推流的用户列表】
   * 1. 用于感知远端推流状态变更
   * 2. 若断网重连期间，有新用户推流/老用户取消推流，当网络恢复后，可通过该函数及时更新远端推流状态
   * @private
   * @param {Object} data
   * @memberof Client
   */
  onPublishedUserList(data: any) {
    // 避免进房成功前，收到 cmd 23 导致漏掉了事件
    if (!this.isJoined) return;

    let hasLocalUser = false;
    const userList = data.data.userList || [];
    const mixRobotList = data.data.mixRobotList || [];

    const tempUserList: BasePublishedUserInfo[] = [];

    for (const item of userList) {
      // 跨房连麦时，远端关闭所有上行后，还会有一个 CROSS_ROOM_BIT(128) flag，如果 flag 是 128 代表跨房连麦的用户没有推流了，需要过滤掉。
      if (item.flag === CROSS_ROOM_BIT) continue;
      const { userId, srcTinyId: tinyId, flag, fromType } = item;
      if (userId === this.userId) {
        hasLocalUser = true; // 优化点：顺手标记，省去最后的 find
        if (this.uplinkConnection) this.uplinkConnection.flag = flag;
        if (this.localPublishFlag !== flag) {
          this.localPublishFlag = flag;
          this.emit(RoomEvent.LOCAL_PUBLISH_FLAG_CHANGED, flag);
        }
      }

      tempUserList.push({ userId, tinyId, flag, fromType });
    }

    const tempRobotList = mixRobotList.map(({ userId, srcTinyId: tinyId, flag, mixUserList, fromType }: any) => ({
      userId, tinyId, flag, isRobot: true, mixUserList, fromType
    }));

    const publishedUserList = [...tempRobotList, ...tempUserList];

    publishedUserList.forEach(({ userId }) => {
      const downlinkConnection = this.remotePublishedUserMap.get(userId);
      if (downlinkConnection) this.checkSubscribeBigSmallVideo(downlinkConnection);
    });
    // PSTN 流
    if (data.data.fakeMixUser) {
      data.data.fakeMixUser.tinyId = data.data.fakeMixUser.srcTinyId;
      publishedUserList.push(data.data.fakeMixUser);
    }

    innerEmitter.emit(INNER_EVENT.RECEIVED_PUBLISHED_USER_LIST, { room: this, publishedUserList });

    if (!hasLocalUser) {
      this.localPublishFlag = 0;
      this.emit(RoomEvent.LOCAL_PUBLISH_FLAG_CHANGED, 0);
    }
    this.userManager.setRemotePublishedUserList(publishedUserList);
  }

  private closeUplink(reason = CONNECTION_CLOSED_REASON.LOCAL_UNPUBLISH) {
    if (this.uplinkConnection) {
      // 正在推流，向后台发送 unpublish 信令后，再关闭连接。
      if (this.localTracks.size > 0) {
        this.uplinkConnection.doUnpublish().catch(() => {});
      }
      this.uplinkConnection.close(reason);
      if (reason === CONNECTION_CLOSED_REASON.LOCAL_LEAVE) {
        this.uplinkConnection.destroy();
        this.uplinkConnection = null;
      }
      if (this.uplinkConnection instanceof UplinkConnection) {
        this.uplinkConnection = null;
      }
    }
    this.localTracks.forEach(localTrack => localTrack.unpublish());
    this.localTracks.clear();
  }

  /**
   * 创建 DownlinkConnection，用户订阅时，建立 PeerConnection
   * @private
   * @param {Object} options
   * @param {String} options.userId
   * @param {String} options.tinyId
   * @memberof Client
   */
  createDownlinkConnection({ userId, tinyId, flag, isRobot, fromType }: { userId: string; tinyId: string; flag: number; isRobot?: boolean; fromType?: number; }) {
    const downlinkConnection = new (this.singlePC ? DownlinkConnectionSPC : DownlinkConnection)({
      userId,
      tinyId,
      room: this,
      signalChannel: this.signalChannel!,
      enableSEI: this.enableSEI,
      flag,
      isRobot,
      fromType
    });
    this.userManager.addRemotePublishedUser(downlinkConnection);
    this.installDownlinkEvents(downlinkConnection, userId);
    this.emit(RoomEvent.REMOTE_PUBLISHED, downlinkConnection);
  }

  closeDownLinkConnection(userId: string, reason = CONNECTION_CLOSED_REASON.REMOTE_UNPUBLISH) {
    const downlinkConnection = this.remotePublishedUserMap.get(userId);
    if (downlinkConnection) {
      downlinkConnection.close(reason);
      this.emit(RoomEvent.REMOTE_UNPUBLISHED, downlinkConnection);
    }
  }

  /**
   *
   * @private
   * @param {DownlinkConnection} downlinkConnection
   * @param {String} userId
   * @param {String} tinyId
   * @memberof Client
   */
  installDownlinkEvents(downlinkConnection: DownlinkConnection | DownlinkConnectionSPC, userId: string) {
    downlinkConnection.on(ConnectionEvent.ERROR, error => {
      const errorCode = error.getCode();
      if (errorCode === ErrorCode.ICE_TRANSPORT_ERROR) {
        return;
      }
      // 下行重连失败，清理相关下行
      if (errorCode === ErrorCode.DOWNLINK_RECONNECTION_FAILED) {
        this.closeDownLinkConnection(userId);
      }
      this.emit(RoomEvent.ERROR, error);
    });
    downlinkConnection.on(ConnectionEvent.CONNECTION_STATE_CHANGED, event => {
      this.emit(RoomEvent.MEDIA_CONNECTION_STATE_CHANGED, { ...event, userId: downlinkConnection.userId });
    });
  }

  /**
   * 在 live 模式且为观众角色的 client 中，需要定时同步用户列表。
   * 以解决 live 模式下，观众收不到主播进退房通知的问题。
   * ref: http://tapd.oa.com/TerWebG/bugtrace/bugs/view/1020396022085576201
   * [2021-11-24]: 信令改造, 无论针对 live 还是 rtc 模式, 都需要每 10s 拉取一下, 服务端内部走 udp 会丢掉信令
   * https://iwiki.woa.com/pages/viewpage.action?pageId=1109917349
   * @private
   * @memberof Client
   */
  startSyncUserListInterval() {
    if (this._syncUserListInterval !== -1) {
      return;
    }
    this._syncUserListInterval = timer.run(TIMER_TYPE.RIC, this.syncUserList.bind(this)); // 10000ms
  }

  stopSyncUserListInterval() {
    timer.clearTask(this._syncUserListInterval);
    this._syncUserListInterval = -1;
  }

  /**
   * 获取用户列表
   * 调用时机：进房后、WS 重连成功后
   * @private
   * @memberof Client
   */
  syncUserList() {
    return this.getUserList()
      .then(userList => {
        this.userManager.setUserList(userList);
      })
      .catch(error => {
        this._log.debug(`sync user list failed: ${error}`);
      });
  }

  // 获取用户列表低概率出现没有回包信令，后台暂时未定位出原因
  // SDK 先加上重试
  // ref: http://tapd.oa.com/TerWebG/prong/stories/view/1020396022861834973
  // @promiseRetryDecorator({
  //   retries: 3
  // })
  getUserList() {
    if (!this.signalChannel?.isConnected) return Promise.reject('not connected');
    return this.signalChannel
      .sendWaitForResponse({
        command: SignalCommand.GET_USER_LIST,
        responseCommand: SignalEvent.USER_LIST_RES,
        enableLog: false,
        timeout: 2000
      })
      .then(({ data }: any) => {
        const { code, message } = data;
        if (code === 0) {
          const userList = ((data.data && data.data.userList) || [])
            .map(({ userId, srcTinyId: tinyId, role, fromType }: {
              userId: string;
              srcTinyId: string;
              role: number;
              fromType: number;
            }) => ({ userId, tinyId, role, fromType }));
          return userList;
        }
        throw generateErrorMessage({
          key: ErrorName.SIGNAL_RESPONSE_FAILED,
          data: { signalResponse: SignalEvent.USER_LIST_RES, code, message }
        });
      });
  }

  getAllConnections() {
    const connections: Connection[] = [...this.remotePublishedUserMap.values()] as Connection[];
    if (this.uplinkConnection) {
      connections.push(this.uplinkConnection as UplinkConnection);
    }
    return connections;
  }

  /**
   * 判断 relay 媒体服务器是否可能已经故障。判断条件：
   * 1. 之前有 PeerConnection 成功连接过
   * 2. 所有的 Connection 重连了超过 6 次，
   * 3. websocket 信令通道处于 online 状态
   * 则后台 relay 可能已经宕机，此时需要通过重进房来尝试调度到新的 relay。
   * @private
   * @return {Boolean}
   * @memberof Client
   */
  isRelayMaybeFailed() {
    if (this.signalChannel && !this.signalChannel.isOnline) {
      return false;
    }
    if (!isPeerConnectionConnectedBefore) {
      return false;
    }
    if (this.singlePC) {
      return this.singlePC.reconnectionCount > 6;
    }
    const connections = this.getAllConnections();
    if (connections.length === 0) {
      return false;
    }
    for (let i = 0; i < connections.length; i++) {
      if (connections[i].getReconnectionCount() < 6) {
        return false;
      }
    }

    return true;
  }

  /**
   * 笔记本合盖后恢复 PeerConnection 重连
   * 调用时机：Websocket rebuild session 成功、重进房成功
   * ref: http://tapd.oa.com/20396022/prong/stories/view/1020396022868179443
   * @private
   * @memberof Client
   */
  checkConnectionsToReconnect() {
    if (this.singlePC) {
      if (this.singlePC.getPeerConnection()?.connectionState === PEER_CONNECTION_STATE.CLOSED && !this.singlePC.isReconnecting) {
        this._log.warn('spc pc is closed but not reconnect');
        this.singlePC.startReconnection();
      }
    } else {
      const connections = this.getAllConnections();
      connections.forEach(connection => {
        if (connection instanceof Connection && !connection.getIsReconnecting()) {
          const peerConnection = connection.getPeerConnection();
          if (peerConnection && peerConnection.connectionState === PEER_CONNECTION_STATE.CLOSED) {
            this._log.warn(`[${connection.getUserId()}] pc is closed but not reconnect`);
            connection.startReconnection();
          }
        }
      });
    }
  }

  // 单 PC 回退到多 PC
  async fallbackToMPC() {
    this._log.warn('fallback to multi pc');
    eventLogger.uploadEvent({ log: 'stat-fallback', userId: this.userId });
    this.enableSPC = false;
    this.singlePC?.close();
    this.singlePC = null;

    // 多 PC 的进房信令不支持竞速
    if (this.isJoined) {
      await this.reJoin();
    }
    if (this.uplinkConnection) {
      const prevUplinkConnection = this.uplinkConnection;
      this.uplinkConnection = new UplinkConnection({
        userId: this.userId,
        tinyId: this.tinyId!,
        room: this,
        signalChannel: this.signalChannel!,
        enableSEI: this.enableSEI,
        enableDtx: this.isEnableDtx()
      });
      if (prevUplinkConnection.isMainStreamPublished) {
        await this.uplinkConnection.publish({
          localAudioTrack: prevUplinkConnection.localMainAudioTrack,
          localVideoTrack: prevUplinkConnection.localMainVideoTrack,
          isAuxiliary: false
        });
      }
      if (prevUplinkConnection.isAuxStreamPublished) {
        await this.uplinkConnection.publish({
          localAudioTrack: prevUplinkConnection.localAuxAudioTrack,
          localVideoTrack: prevUplinkConnection.localAuxVideoTrack,
          isAuxiliary: true
        });
      }
      prevUplinkConnection.close();
    }
    for (const downlinkConnection of [...this.remotePublishedUserMap.values()]) {
      const newDownlinkConnection = new DownlinkConnection({
        userId: downlinkConnection.userId,
        tinyId: downlinkConnection.tinyId,
        room: this,
        signalChannel: this.signalChannel!,
        enableSEI: this.enableSEI,
        flag: downlinkConnection.flag,
        remoteAudioTrack: downlinkConnection.remoteAudioTrack,
        remoteVideoTrack: downlinkConnection.remoteVideoTrack,
        remoteAuxiliaryTrack: downlinkConnection.remoteAuxiliaryTrack,
        enableDtx: this.isEnableDtx()
      });
      this.installDownlinkEvents(newDownlinkConnection, downlinkConnection.userId);
      this.remotePublishedUserMap.set(downlinkConnection.userId, newDownlinkConnection);
      if (downlinkConnection.isMainStreamSubscribed) {
        await newDownlinkConnection.subscribe(downlinkConnection.subscribeState, RemoteStreamType.Main);
      }
      if (downlinkConnection.isAuxStreamSubscribed) {
        await newDownlinkConnection.subscribe(downlinkConnection.subscribeState, RemoteStreamType.Aux);
      }
    }
  }

  /**
   * 销毁 Client 实例 <br/>
   *
   * 调用时机：
   *  - 在退房之后，若业务侧无需再使用 client 时，需调用该接口及时销毁 client 实例，释放相关资源。
   *  - 当用户网络不好时，{@link Client#join client.join} 可能花费较长时间，可调用该接口中断 client.join 调用流程。<br/>
   *
   * 注意：
   *  - 销毁后的 client 实例不可再继续使用。
   *  - 已进房的情况下，需先调用 {@link Client#leave client.leave} 接口退房成功后，才能调用该接口销毁 client。
   * @since v4.13.0
   * @example
   * let client = TRTC.createClient({ sdkAppId, userId, userSig, mode: 'rtc' });
   * await client.join({ roomId: 123456 });
   * // 通话结束时
   * await client.leave();
   *
   * // 若后续无需再使用该 client，则销毁 client，并释放引用。
   * client.destroy();
   * client = null;
   *
   * @example
   * let client = TRTC.createClient({ sdkAppId, userId, userSig, mode: 'rtc' });
   *
   * // 假设业务逻辑需要在5s内完成进房，否则认为进房超时。
   * const id = setTimeout(() => {
   *  // 销毁 client 实例，中止 client.join 流程。
   *  client.destroy();
   *  client = null;
   * }, 5000)
   *
   * try {
   *  await client.join({ roomId: 123456 });
   * } catch(error) {}
   *
   * clearTimeout(id);
   * @memberof Client
   */
  destroy() {
    if (this.isDestroyed) {
      return;
    }
    if (this.signalChannel) {
      this._log.info('destroying SignalChannel');
      this.signalChannel.close();
      this.signalChannel = null;
    }
    super.destroy();
    // 中断正在执行的 client.join 调用。
    if (this._joinReject) {
      this._joinReject(new RtcError({
        code: ErrorCode.INVALID_OPERATION,
        message: generateErrorMessage({
          key: ErrorName.CLIENT_DESTROYED,
          data: { funName: 'join' }
        })
      }));
      this.clearJoinTimeout();
      this.reset();
    }

    // 销毁防火墙检测器
    this.firewallDetector.destroy();

    this.removeAllListeners();
    this.healthDetector.destroy();
    timer.clearTask(this._audioVolumeIntervalId);
  }

  /**
   * 切换用户角色，仅在 'live' 互动直播模式下生效。
   *
   * 互动直播模式下，一个用户可能需要在“观众”和“主播”之间来回切换。
   * 您可以通过 {@link Room#join join()} 中的 role 字段确定角色，也可以通过 switchRole 在进房后切换角色。
   * - 观众切换为主播，调用 client.switchRole('anchor') 将用户角色转换为 'anchor' 主播角色，之后按需调用 {@link Client#publish publish()} 发布本地流。
   * - 主播切换为观众，调用 client.switchRole('audience') 将用户角色转换为 'audience' 观众角色，此时如果有已发布的本地流，SDK 会取消发布本地流。
   * > !
   * > - 该接口需要在进房成功后才可以调用。
   * @param {string} role 用户角色
   * - 'anchor' 主播，可以发布本地流，单个房间里最多支持 50 个主播同时发布本地流。
   * - 'audience' 观众，不能发布本地流，只能观看远端流，单个房间里的观众人数没有上限。
   * @returns {Promise}
   * @throws RtcError
   * @memberof Room
   * @example
   * // 进房成功后
   * // live 互动直播模式下，观众切换为主播
   * await client.switchRole('anchor');
   * // 观众角色切换为主播，开始推流
   * await client.publish(localStream);
   *
   * // live 互动直播模式下，主播切换为观众
   * await client.switchRole('audience');
   */
  async switchRole(role: UserRole): Promise<void> {
    if (this.role === role) {
      return;
    }
    if (role === UserRole.AUDIENCE && this.uplinkConnection) {
      this.closeUplink(CONNECTION_CLOSED_REASON.SWITCH_ROLE);
    }
    await this.doSwitchRole(role);
  }

  doSwitchRole(role: UserRole) {
    const changeRoleCommand = {
      command: SignalCommand.SWITCH_ROLE,
      data: {
        role: role === UserRole.ANCHOR ? UserRoleNumber.ANCHOR : UserRoleNumber.AUDIENCE,
        privateMapKey: this.privateMapKey,
        latencyLevel: this.latencyLevel
      },
      responseCommand: SignalEvent.SWITCH_ROLE_RES,
      retries: 1
    };
    this._log.info(`switchRole signal data: ${JSON.stringify(changeRoleCommand.data)}`);
    return this.signalChannel!
      .sendWaitForResponseWithRetry(changeRoleCommand)
      .then(event => {
        const { code, message } = event.data;
        if (code !== 0) {
          throw new RtcError({
            code: ErrorCode.SWITCH_ROLE_FAILED,
            message: generateErrorMessage({
              key: ErrorName.SWITCH_ROLE_FAILED,
              data: { message, code }
            })
          });
        }
        this.role = role;
      })
      .catch((error: RtcError | Error) => {
        if (error instanceof RtcError && error.getCode() === ErrorCode.API_CALL_TIMEOUT) {
          error = new RtcError({
            code: ErrorCode.SWITCH_ROLE_FAILED,
            message: generateErrorMessage({ key: ErrorName.SWITCH_ROLE_TIMEOUT })
          });
        }
        this._log.error(error);
        throw error;
      });
  }

  async subscribeDataChannel() {
    if (this.remotePublishedUserMap.size === 0) return;
    const asrRobotUsers = [...this.remotePublishedUserMap.values()].filter((user: PublishedUser) => user.fromType === ASR_ROBOT_FROM_TYPE);
    this._log.info('subscribeDataChannel', asrRobotUsers.map(user => user.userId));
    await Promise.all(asrRobotUsers.map(async (downlink: DownlinkConnection | DownlinkConnectionSPC) => {
      try {
        await downlink.subscribe({
          ...downlink.subscribeState,
          datachannel: true
        }, RemoteStreamType.Main);
      } catch (error) {
        this._log.error('subscribeDataChannel failed:', downlink.userId, error);
      }
    }));
  }

  async unsubscribeDataChannel() {
    if (this.remotePublishedUserMap.size === 0) return;
    const asrRobotUsers = [...this.remotePublishedUserMap.values()].filter((user: PublishedUser) => user.fromType === ASR_ROBOT_FROM_TYPE);
    this._log.info('unsubscribeDataChannel', asrRobotUsers.map(user => user.userId));
    await Promise.all(asrRobotUsers.map((downlink: DownlinkConnection | DownlinkConnectionSPC) => downlink.unsubscribeDataChannel()));
  }

  private _initUplinkConnection() {
    this.uplinkConnection = this.singlePC ? new UplinkConnectionSPC({
      userId: this.userId,
      tinyId: this.tinyId!,
      room: this
    }) : new UplinkConnection({
      userId: this.userId,
      tinyId: this.tinyId!,
      room: this,
      signalChannel: this.signalChannel!,
      enableSEI: this.enableSEI
    });

    this.uplinkConnection.on(ConnectionEvent.CONNECTION_STATE_CHANGED, event => {
      this.emit(RoomEvent.MEDIA_CONNECTION_STATE_CHANGED, { ...event, userId: this.userId });
    });
    this.uplinkConnection.on(ConnectionEvent.ERROR, (error: RtcError) => {
      const errorCode = error.getCode();
      if (errorCode === ErrorCode.ICE_TRANSPORT_ERROR) {
        return;
      }
      // 上行重连失败，清理上行状态，让接入侧可以调用 publish 重新推流
      if (errorCode === ErrorCode.UPLINK_RECONNECTION_FAILED) {
        this.closeUplink();
      }
      this.emit(RoomEvent.ERROR, error);
    });
  }

  @addCallQueueValidArgs((localTrack: ILocalTrack) => localTrack.mediaType)
  @beforePublish()
  // 推流无限重试
  @addPromiseRetry({
    settings: { retries: getRetryCount, timeout: retryCount => getReconnectionTimeout(retryCount) },
    onError(this: Room, error, retry, reject, [localTrack]: ILocalTrack[]) {
      if (error.message?.includes('timeout')) {
        this._log.warn(`publish ${localTrack.strMediaType} timeout`, error);
        retry();
      } else {
        this._log.error(`publish ${localTrack.strMediaType} failed: ${error}`);
        reject(error);
        innerEmitter.emit(INNER_EVENT.PUBLISH_FAILED, { room: this });
      }
    }
  })
  async publish(localTrack: ILocalTrack): Promise<void> {
    if (!this.uplinkConnection) this._initUplinkConnection();
    const logStreamType = `${localTrack.streamType} ${localTrack.isAudio && localTrack.isScreen ? 'screen' : ''}${localTrack.kind}`;
    this._log.info(`publish() => ${logStreamType}`);
    await this.singlePC?.waitForPeerConnectionConnected(); // 等连接成功后再推流，防止在未连接成功时，localTrack.publish 定时器就启动了，导致 publish 超时
    await this.uplinkConnection!.publish({
      localAudioTrack: localTrack instanceof LocalAudioTrack ? localTrack : null,
      localVideoTrack: localTrack instanceof LocalVideoTrack ? localTrack : null,
      isAuxiliary: localTrack.streamType === RemoteStreamType.Aux
    });
  }

  @removePromiseRetry({ fnName: 'publish' })
  @addCallQueueValidArgs((localTrack: ILocalTrack) => localTrack.mediaType)
  @beforeUnpublish()
  @after(function (this: Room) {
    if (this.localTracks.size === 0 && isGetSendersSupported()) {
      this.singlePC?.getPeerConnection()?.getSenders()
        .forEach(sender => sender.track && sender.replaceTrack(null));
    }
  })
  async unpublish(localTrack: ILocalTrack): Promise<void> {
    if ((this.scene === Scene.LIVE && this.role !== UserRole.ANCHOR) ||
    (!this.isMainStreamPublished && !this.isAuxStreamPublished) ||
    !this.uplinkConnection
    ) {
      return;
    }
    // 当 track 不在 published/publishing 状态时，通常说明已经 unpublish 过，此时不需要重复执行。
    // 但如果 publish 过程中被 abort（如 stopLocalAudio 先 close 了 track），track 状态已变为 INIT，
    // 而 uplinkConnection 上仍持有该 track 的引用（SPC_PUBLISH 信令已发到服务器），此时仍需发送 unpublish 信令。
    if (!(localTrack.isPublished || localTrack.isPublishing) && !this.uplinkConnection.isTrackReferenced(localTrack)) {
      return;
    }
    try {
      const logStreamType = `${localTrack.streamType} ${localTrack.isAudio && localTrack.isScreen ? 'screen' : ''}${localTrack.kind}`;
      this._log.info(`unpublish() => ${logStreamType}`);
      await this.uplinkConnection.unpublish({
        localAudioTrack: localTrack instanceof LocalAudioTrack ? localTrack : null,
        localVideoTrack: localTrack instanceof LocalVideoTrack ? localTrack : null
      });
    } catch (error) {
      // ignore
      // unpublish 不会失败，此处 catch error
    }

    if (this.localTracks.size === 0) {
      this.closeUplink(CONNECTION_CLOSED_REASON.LOCAL_UNPUBLISH);
    }
  }

  addTrack(localTrack: ILocalTrack): Promise<void> {
    if (!this.uplinkConnection || !localTrack.mediaTrack) {
      return Promise.resolve();
    }
    const promise = this.uplinkConnection.addTrack(localTrack);
    localTrack.publish(this, promise);
    return promise;
  }

  removeTrack(localTrack: ILocalTrack) {
    if (!this.uplinkConnection || !localTrack.mediaTrack) {
      return Promise.resolve();
    }
    localTrack.unpublish();
    return this.uplinkConnection.removeTrack(localTrack);
  }

  @catchError((error: RtcError) => {
    if (error.code === ErrorCode.API_CALL_ABORTED) return;
    throw error;
  })
  @addCallQueueValidArgs((localTrack: ILocalTrack) => localTrack.userId)
  replaceTrack(localTrack: ILocalTrack): Promise<void> {
    if (!this.uplinkConnection || !localTrack.mediaTrack || !isReplaceTrackSupported()) {
      return Promise.resolve();
    }
    return this.uplinkConnection.replaceTrack(localTrack)
      .then(replaced => {
        replaced && innerEmitter.emit(INNER_EVENT.LOCAL_TRACK_REPLACED, { track: localTrack });
      });
  }

  async setBandWidth(option: BandWidthOption) {
    if (!this.uplinkConnection) return;

    await this.uplinkConnection.setBandwidth(option);
    await this.uplinkConnection.sendMediaSettings();
  }

  async enableSmall(enable: boolean) {
    if (!this.uplinkConnection || !this.uplinkConnection.localMainVideoTrack) return Promise.resolve();
    if (enable && this.uplinkConnection.localMainVideoTrack.small) {
      await this.setBandWidth({ type: NAME.VIDEO, videoType: NAME.SMALL, bandwidth: this.uplinkConnection.localMainVideoTrack.small.bitrate });
    }
    await this.uplinkConnection.enableSmall(enable);
  }

  @addCallQueueValidArgs((...remoteTracks) => remoteTracks[0].userId, (...remoteTracks) => `${remoteTracks[0].userId}|sub`)
  @checkPendingPromise()
  @changeRemoteTrackState()
  @addPromiseRetry({
    settings: { retries: getRetryCount, timeout: retryCount => getReconnectionTimeout(retryCount) },
    onError(this: Room, error, retry, reject, remoteTracks: IRemoteTrack[]) {
      if (error.message.includes('timeout')) {
        this._log.warn('subscribe timeout');
        // 订阅超时会关闭 peerConnection 实例，调用 subscribe 接口若没有 peerConnection 实例，会初始化 peerConnection 实例。
        retry();
      } else {
        const isAbortError = error?.code === ErrorCode.API_CALL_ABORTED;
        this._log[isAbortError ? 'warn' : 'error'](`subscribe failed ${remoteTracks.map(remoteTrack => remoteTrack.strMediaType).join(',')}: ${error}`);
        reject(error);
        innerEmitter.emit(INNER_EVENT.SUBSCRIBE_FAILED, { room: this, remoteTracks });
      }
    }
  })
  async subscribe(...remoteTracks: IRemoteTrack[]): Promise<void> {
    remoteTracks = remoteTracks.filter(remoteTrack => !remoteTrack.isSubscribed);
    if (remoteTracks.length === 0) return;

    const { userId } = remoteTracks[0];
    const downlinkConnection = this.remotePublishedUserMap.get(userId);
    if (!downlinkConnection) return;

    // whip 拉流, 直接标记为已订阅
    if (this.whipQuickStartConfig && userId === this.whipQuickStartConfig.remoteUserId) {
      const whipTracks = remoteTracks.filter(t => t.mediaTrack);
      if (whipTracks.length > 0) {
        this._log.info(`subscribe() => skip quickStart remote user ${userId}`);
        const subscribedPromise = Promise.resolve();
        whipTracks.forEach(t => t.subscribe(subscribedPromise));
        remoteTracks = remoteTracks.filter(t => !t.mediaTrack);
        if (remoteTracks.length === 0) return;
      }
    }

    const streamType = remoteTracks.find(remoteTrack => remoteTrack.mediaType === MediaType.AUX_VIDEO)
      ? RemoteStreamType.Aux
      : RemoteStreamType.Main;

    try {
      const subscribeState = { ...downlinkConnection.subscribeState };

      remoteTracks.forEach(remoteTrack => {
        switch (remoteTrack.mediaType) {
          case MediaType.AUDIO:
            subscribeState.audio = true;
            break;
          case MediaType.BIG_VIDEO:
            subscribeState.video = true;
            break;
          case MediaType.SMALL_VIDEO:
            subscribeState.smallVideo = true;
            break;
          case MediaType.AUX_VIDEO:
            subscribeState.auxiliary = true;
            break;
        }
      });
      const record = this._changeBigSmallRecords.get(userId);
      if (record && record.options.smallVideo) {
        if (downlinkConnection.muteState.hasSmall && subscribeState.video) {
          subscribeState.video = false;
          subscribeState.smallVideo = true;
        }
      }
      innerEmitter.emit(INNER_EVENT.SUBSCRIBE_START, {
        room: this,
        streamType,
        remotePublishedUser: downlinkConnection,
        subscribeState
      });

      this._log.info(`subscribe() => ${userId} ${streamType} ${remoteTracks.map(remoteTrack => remoteTrack.strMediaType).join(',')} [${getSubscribedKeys(subscribeState)}] prev: [${getSubscribedKeys(downlinkConnection.subscribeState)}]`);
      await downlinkConnection.subscribe(subscribeState, streamType);

      this._log.info(`subscribe ${userId} ${streamType} done`);
      for (const remoteTrack of remoteTracks) {
        if (!remoteTrack.mediaTrack) {
          await remoteTrack.waitHasMediaTrack();
        }
      }

      innerEmitter.emit(INNER_EVENT.SUBSCRIBE_SUCCESS, {
        room: this,
        streamType,
        remotePublishedUser: downlinkConnection
      });
    } catch (error: any) {
      const errorCode = error instanceof RtcError ? error.getCode() : ErrorCode.UNKNOWN;
      let rtcError = error;
      if (!(error instanceof RtcError)) {
        // 需要客户处理，不可恢复的异常
        rtcError = new RtcError({
          code: errorCode,
          message: generateErrorMessage({
            key: ErrorName.SUBSCRIBE_FAILED, data: {
              message: error.message,
              userId,
              streamType
            }
          })
        });
        this._log.error(rtcError);
      } else if (errorCode === ErrorCode.REMOTE_STREAM_NOT_EXIST) {
        // 不需要客户处理
        rtcError = new RtcError({
          code: ErrorCode.API_CALL_ABORTED,
          message: generateErrorMessage({
            key: ErrorName.API_CALL_ABORTED, data: {
              message: error.message,
              userId,
              streamType
            }
          })
        });
        this._log.warn(rtcError);
      }
      throw rtcError;
    }
  }

  @removePromiseRetry({
    fnName: 'subscribe', callback(this: Room, ...remoteTracks: IRemoteTrack[]) {
      if (this.singlePC) return;
      remoteTracks.forEach(remoteTrack => {
        const downlinkConnection = this.remotePublishedUserMap.get(remoteTrack.userId);
        if (downlinkConnection && !downlinkConnection.isMainStreamSubscribed && !downlinkConnection.isAuxStreamSubscribed) {
          downlinkConnection.close(CONNECTION_CLOSED_REASON.LOCAL_UNSUBSCRIBE);
        }
      });
    }
  })
  @addCallQueueValidArgs((...remoteTracks) => remoteTracks[0].userId, (...remoteTracks) => `${remoteTracks[0].userId}|sub`)
  async unsubscribe(...remoteTracks: IRemoteTrack[]): Promise<void> {
    const { userId } = remoteTracks[0];
    const downlinkConnection = this.remotePublishedUserMap.get(userId);
    if (!downlinkConnection) return;

    const streamType = remoteTracks.find(remoteTrack => remoteTrack.mediaType === MediaType.AUX_VIDEO)
      ? RemoteStreamType.Aux
      : RemoteStreamType.Main;
    this._log.info(`unsubscribe() => ${userId} ${streamType} ${remoteTracks.map(i => i.strMediaType).join(',')}`);
    try {
      await downlinkConnection.unsubscribe({ remoteTracks, streamType });
    } catch (error: any) {
      // 对于业务侧 unsubscribe 不会失败，此处拦截 error 不抛出。
      this._log.warn(`unsubscribe() => failed ${error}`);
    }
    remoteTracks.forEach(remoteTrack => {
      remoteTrack.unsubscribe();
      // 取消订阅后，将 remoteVideoTrack.mediaType 重置为 BIG_VIDEO
      if (remoteTrack.mediaType === MediaType.SMALL_VIDEO) {
        remoteTrack.setMediaType(MediaType.BIG_VIDEO);
      }
    });

    innerEmitter.emit(INNER_EVENT.UNSUBSCRIBE_SUCCESS, {
      room: this,
      streamType,
      remotePublishedUser: downlinkConnection
    });
  }

  setEncodedDataProcessingListener(listener: EncodedDataProcessingListener): void {
    throw new Error('Method not implemented.');
  }

  enableAudioVolumeEvaluation(interval = 2000, enableInBackground?: boolean): void {
    if (interval <= 0) {
      this._enableAudioVolumeEvaluation = false;
      timer.clearTask(this._audioVolumeIntervalId);
      return;
    }

    // interval 是整数，最小值为 100
    interval = Math.floor(Math.max(interval, 100));

    // 更新 worklet 的 interval 时间
    innerEmitter.emit(INNER_EVENT.AUDIO_LEVEL_INTERVAL, { interval });

    // 重复调用该接口，设置 interval，需清除的 interval
    if (this._audioVolumeIntervalId) {
      timer.clearTask(this._audioVolumeIntervalId);
    }

    this._enableAudioVolumeEvaluation = true;

    this._audioVolumeIntervalId = timer.run(
      TIMER_TYPE.INTERVAL_IN_WORKER,
      () => {
        if (!VolumeMeter.isRunning) this.updateAudioLevelFromSenderStat(interval, enableInBackground);
        else this.stopUpdateAudioLevelFromSenderStat();
        const result: TRTCVolumeInfo[] = [];
        this.remotePublishedUserMap?.forEach(remotePublishedUser => {
          if (remotePublishedUser.muteState.hasAudio) {
            if (!VolumeMeter.isRunning && remotePublishedUser.muteState.audioAvailable && remotePublishedUser.remoteAudioTrack.isSubscribed) this.updateDownlinkAudioLevelFromReceiver(remotePublishedUser);
            else remotePublishedUser.remoteAudioTrack.volume = 0;
            const volume = Math.floor(remotePublishedUser.remoteAudioTrack.getAudioLevel() * 100);
            result.push({ userId: remotePublishedUser.userId, volume, floatVolume: remotePublishedUser.remoteAudioTrack.getInternalAudioLevel() });
          }
        });
        this.emit(RoomEvent.AUDIO_VOLUME, result);
      }, { delay: interval, backgroundTask: enableInBackground }
    );
  }

  // 在用户没有点击页面的情况下，audioContext 不处于 running 状态， 算不了音量
  // 此时用 audio sender.getStats 的音量代替。
  async updateAudioLevelFromSenderStat(interval: number, runInBackground?: boolean) {
    if (!this.uplinkConnection || !this.uplinkConnection.localMainAudioTrack || this._updateAudioLevelTaskId !== -1) return;
    const sender = this.uplinkConnection.getPeerConnection()?.getSenders()[0];
    if (!sender) return;

    const _interval = Math.max(interval, 500); // 出于性能考虑，最小间隔 500ms
    this._log.warn(`updateAudioLevelFromSenderStat ${_interval}`);
    this._updateAudioLevelTaskId = timer.run(TIMER_TYPE.INTERVAL_IN_WORKER, async () => {
      if (!this.uplinkConnection || !this.uplinkConnection.localMainAudioTrack) {
        this.stopUpdateAudioLevelFromSenderStat();
        return;
      }
      const stats = await sender.getStats();
      if (this._updateAudioLevelTaskId < 0) return;
      const { localMainAudioTrack } = this.uplinkConnection;
      stats.forEach(stat => {
        if (stat.type === 'media-source' && stat.audioLevel) {
          localMainAudioTrack.volume = stat.audioLevel;
        }
      });
    }, { delay: _interval, backgroundTask: runInBackground });
  }

  stopUpdateAudioLevelFromSenderStat() {
    if (this._updateAudioLevelTaskId !== -1) {
      this._log.warn('stopUpdateAudioLevelFromSenderStat');
      timer.clearTask(this._updateAudioLevelTaskId);
      this._updateAudioLevelTaskId = -1;
      if (this.uplinkConnection?.localMainAudioTrack) {
        this.uplinkConnection.localMainAudioTrack.volume = 0;
      }
    }
  }

  updateDownlinkAudioLevelFromReceiver(downlinkConnection: DownlinkConnectionSPC | DownlinkConnection) {
    const { audioReceiver } = downlinkConnection;
    if (!IS_GET_SYNCHRONIZATION_SOURCES_SUPPORTED || !audioReceiver) return;
    const audioLevel = audioReceiver.getSynchronizationSources()[0]?.audioLevel;
    // 测试这种方式获取的 audioLevel 不太灵敏，对着麦克风吹气也只到 0.5，此处 * 2 来放大。
    if (isNumber(audioLevel)) downlinkConnection.remoteAudioTrack.volume = Math.min(audioLevel * 2, 1);
    else audioReceiver.getStats().then(stats => { // native 的音频流，可能 rtp 没有音频扩展，则用 getStats 获取音量兜底。
      stats.forEach(stat => {
        if (stat.type === 'inbound-rtp' && isNumber(stat.audioLevel)) {
          downlinkConnection.remoteAudioTrack.volume = stat.audioLevel;
        }
      });
    });
  }

  async getLocalAudioStats() {
    const result: LocalAudioStats = {};
    result[this.userId] = { bytesSent: 0, packetsSent: 0, audioLevel: 0 };
    if (this.uplinkConnection?.localMainAudioTrack) {
      result[this.userId] = this.uplinkConnection.localMainAudioTrack.stat;
    }
    return result;
  }

  async getLocalVideoStats() {
    const result: LocalVideoStats = {};
    result[this.userId] = this.uplinkConnection?.localMainVideoTrack?.stat || {
      bytesSent: 0,
      packetsSent: 0,
      framesEncoded: 0,
      framesSent: 0,
      frameWidth: 0,
      frameHeight: 0,
      fpsCapture: 0
    };
    return result;
  }

  async getTransportStats() {
    const result: TransportStats = { rtt: 0, downlinksRTT: {} };
    if (this.uplinkConnection) {
      const stats = await this._stats.getSenderStats(this.uplinkConnection, this);
      result.rtt = stats.rtt;
    }
    for (const [, value] of this.remotePublishedUserMap) {
      const stats = await this._stats.getReceiverStats(value);
      result.downlinksRTT[stats.userId] = stats.rtt;
    }
    return result;
  }

  async getRemoteVideoStats(type: RemoteStreamType) {
    const result: RemoteVideoStats = {};
    for (const [userId, remotePublishedUser] of this.remotePublishedUserMap) {
      if (type === RemoteStreamType.Main && remotePublishedUser.muteState.hasVideo) {
        result[userId] = remotePublishedUser.remoteVideoTrack.stat;
      }
      if (type === RemoteStreamType.Aux && remotePublishedUser.muteState.hasAuxiliary) {
        result[userId] = remotePublishedUser.remoteAuxiliaryTrack.stat;
      }
    }
    return result;
  }

  async getRemoteAudioStats() {
    const result: RemoteAudioStats = {};
    for (const [userId, remotePublishedUser] of this.remotePublishedUserMap) {
      if (remotePublishedUser.muteState.hasAudio) {
        result[userId] = remotePublishedUser.remoteAudioTrack.stat;
      }
    }
    return result;
  }

  setTurnServer(options: TurnServerOptions | TurnServerOptions[], iceTransportPolicy?: RTCIceTransportPolicy) {
    this._log.info(`set turn server: ${JSON.stringify(options)} ${iceTransportPolicy || ''}`);
    const turnServers = [];
    if (Array.isArray(options)) {
      options.forEach(item => turnServers.push(Util.getTurnServer(item)));
    } else if (Util.isPlainObject(options)) {
      // 向下兼容（v4.8.5 之前版本），支持传入对象
      turnServers.push(Util.getTurnServer(options));
    }
    this._turnServers = turnServers;
    if (iceTransportPolicy) {
      this._iceTransportPolicy = iceTransportPolicy;
    }
  }

  sendStartMixTranscode(data: StartMixTranscodeData) {
    return this.signalChannel!.sendWaitForResponse({
      command: SignalCommand.START_MIX_TRANSCODE,
      data,
      timeout: 5000,
      responseCommand: SignalEvent.START_MIX_TRANSCODE_RES,
      commandDesc: 'startMixTranscode'
    }).catch(error => {
      if (error.code !== ErrorCode.API_CALL_ABORTED) throw error;
    }) as Promise<SignalResponse>;
  }

  sendStopMixTranscode(data: StopMixTranscodeData) {
    return this.signalChannel!.sendWaitForResponse({
      command: SignalCommand.STOP_MIX_TRANSCODE,
      data,
      timeout: 5000,
      responseCommand: SignalEvent.STOP_MIX_TRANSCODE_RES,
      commandDesc: 'stopMixTranscode'
    }).catch(error => {
      if (error.code !== ErrorCode.API_CALL_ABORTED) throw error;
    }) as Promise<SignalResponse>;
  }

  sendStartPublishCDN(data: StartPublishTencentCDNData | StartPublishGivenCDNData, isTencentCDN = true) {
    return this.signalChannel!.sendWaitForResponse({
      command: isTencentCDN ? SignalCommand.START_PUBLISH_TENCENT_CDN : SignalCommand.START_PUBLISH_GIVEN_CDN,
      data,
      timeout: 5000,
      responseCommand: isTencentCDN ? SignalEvent.START_PUBLISH_TENCENT_CDN_RES : SignalEvent.START_PUBLISH_GIVEN_CDN_RES,
      commandDesc: 'startPublishCDN'
    }).catch(error => {
      if (error.code !== ErrorCode.API_CALL_ABORTED) throw error;
    }) as Promise<SignalResponse>;
  }

  sendStopPublishCDN(data: StopPublishTencentCDNData | StopPublishGivenCDNData, isTencentCDN = true) {
    return this.signalChannel!.sendWaitForResponse({
      command: isTencentCDN ? SignalCommand.STOP_PUBLISH_TENCENT_CDN : SignalCommand.STOP_PUBLISH_GIVEN_CDN,
      data,
      timeout: 5000,
      responseCommand: isTencentCDN ? SignalEvent.STOP_PUBLISH_TENCENT_CDN_RES : SignalEvent.STOP_PUBLISH_GIVEN_CDN_RES,
      commandDesc: 'stopPublishCDN'
    }).catch(error => {
      if (error.code !== ErrorCode.API_CALL_ABORTED) throw error;
    }) as Promise<SignalResponse>;
  }

  sendStartPushStreamToRoom(data: StartPublishMixStreamToRoomData) {
    return this.signalChannel!.sendWaitForResponse({
      command: SignalCommand.START_PUBLISH_CDN_STREAM,
      data,
      timeout: 5000,
      responseCommand: SignalEvent.START_PUBLISH_CDN_STREAM_RES,
      commandDesc: 'startPublishCDNStream'
    }).catch(error => {
      if (error.code !== ErrorCode.API_CALL_ABORTED) throw error;
    }) as Promise<SignalResponse>;
  }

  sendUpdatePushStreamToRoom(data: UpdatePublishMixStreamToRoomData) {
    return this.signalChannel!.sendWaitForResponse({
      command: SignalCommand.UPDATE_PUBLISH_CDN_STREAM,
      data,
      timeout: 5000,
      responseCommand: SignalEvent.UPDATE_PUBLISH_CDN_STREAM_RES,
      commandDesc: 'updatePublishCDNStream'
    }).catch(error => {
      if (error.code !== ErrorCode.API_CALL_ABORTED) throw error;
    }) as Promise<SignalResponse>;
  }

  sendStopPushStreamToRoom(data: StopPublishMixStreamToRoomData) {
    return this.signalChannel!.sendWaitForResponse({
      command: SignalCommand.STOP_PUBLISH_CDN_STREAM,
      data,
      timeout: 5000,
      responseCommand: SignalEvent.STOP_PUBLISH_CDN_STREAM_RES,
      commandDesc: 'stopPublishCDNStream'
    }).catch(error => {
      if (error.code !== ErrorCode.API_CALL_ABORTED) throw error;
    }) as Promise<SignalResponse>;
  }

  /**
   * 自定义信令通道，以 kv 形式上报功能位使用情况。
   * 规则：每成功使用一次，即上报一次
   * tapd: https://tapd.woa.com/10155901/prong/stories/view/1010155901887078735
   *
   * @param {AbilityStatusReportData} data
   */
  sendAbilityStatus(data: AbilityStatusReportData) {
    this.signalChannel?.sendWaitForResponse({
      command: SignalCommand.ABILITY_STATUS_REPORT,
      data,
      timeout: 5000,
      responseCommand: SignalEvent.ABILITY_STATUS_REPORT_RESULT,
      commandDesc: 'ability status report'
    }).catch((e: any) => { });
  }

  getIceServers(backIceServers?: RTCIceServer[]) {
    // 如果客户设置了 turnServer，则使用客户设置的
    if (this._turnServers.length > 0) {
      return this._turnServers;
    }
    // 如果调度结果中会返回了 iceServers，则使用调度结果
    if (this.scheduleResult.iceServers?.length) {
      return this.scheduleResult.iceServers;
    }
    // 如果有重连时指定的 iceServers，则使用重连时指定的
    if (backIceServers?.length) {
      return backIceServers;
    }
    // 如果进房信令返回了 iceServers，则使用进房信令的
    if (this._iceServersFromJoin?.length) {
      return this._iceServersFromJoin;
    }
    return [];
  }

  getIceTransportPolicy() {
    if (this.forceRelay) return 'relay';
    // 如果客户设置了 policy，则用客户设置的；否则使用后台调度服务设置；默认为 'all'
    return this._iceTransportPolicy || this.scheduleResult.iceTransportPolicy || 'all';
  }

  getLogger() {
    return this._log;
  }

  enableAIVoice() {
    throw new Error('Method not implemented.');
  }

  getSignalChannelUrl() {
    const result = {
      mainUrl: '',
      backupUrl: ''
    };
    // 1. URL 后设置了 trtc_env 参数，则返回指定的环境。测试使用。
    const env = Util.getEnv();
    if (env) {
      result.mainUrl = `wss://${Util.getTestSignalDomain(env)}`;
      result.backupUrl = result.mainUrl;
    } else if (this._whipRebuildParams) {
      const wsDomain = this.whipQuickStartConfig?.domain || this.scheduleResult.domains?.[0];
      result.mainUrl = `wss://${wsDomain}`;
      result.backupUrl = result.mainUrl;
    } else if (this.proxy_ws) {
      // 2. 调用了 client.setProxyServer 接口，则返回设置的 url
      result.mainUrl = this.proxy_ws;
      result.backupUrl = result.mainUrl;
    } else if (this.proxy_unified) {
      result.mainUrl = `wss://${this.proxy_unified}`;
      result.backupUrl = result.mainUrl;
    } else if (Array.isArray(this.scheduleResult.domains) && this.scheduleResult.domains.length > 0) {
      // 3. 若有调度域名，则用调度域名
      result.mainUrl = `wss://${this.scheduleResult.domains[0]}`;
      result.backupUrl = result.mainUrl;
      if (this.scheduleResult.domains[1]) {
        result.backupUrl = `wss://${this.scheduleResult.domains[1]}`;
      }
    }
    return result;
  }

  getSignalInfo() {
    return this.signalChannel?.getSignalInfo() || { clientIp: '', relayIp: '' };
  }

  /**
   * reset room
   * 清理内部状态，关闭连接
   * @private
   * @memberof Client
   */
  reset(callByLeave = false) {
    this.stopSyncUserListInterval();
    this.stopHeartbeat();
    this.closeConnections();

    this.clearNetworkQuality();

    // terminate uplink w/o notifying remote server
    this.closeUplink(CONNECTION_CLOSED_REASON.LOCAL_LEAVE);

    this.cleanupWhipResources();

    if (this.signalChannel) {
      // keepAlive 在退房后保持
      if (callByLeave && this.signalChannel.keepAlive && this.signalChannel.isConnected) {
        this.signalChannel.stopKeepAliveIn(3600);
      } else {
        this.signalChannel.close();
        this.setSignalChannel(null);
      }
    }
    this.localPublishFlag = 0;
    this.heartbeatCount = 0;
    this._stats.reset();
    this.userManager.clear();
    this.userManager.removeAllListeners();
    if (this.singlePC) {
      this.singlePC.close();
      this.singlePC = null;
    }
    this.scheduleResult = {
      domains: null,
      iceServers: null,
      iceTransportPolicy: null,
      trtcAutoConf: null
    };
    // 重置预连接状态
    this.clearPreconnectTimeout();
    this.preconnectPromise = null;
  }

  async preconnect(sdkAppId: number, userId: string, userSig: string, frameWorkType: number, roomId?: number, strRoomId?: string): Promise<void> {
    if (this.isJoined) {
      this._log.warn('already joined room');
      return;
    }
    if (!roomId && !strRoomId) {
      throw new RtcError({
        code: ErrorCode.INVALID_OPERATION,
        message: 'roomId or strRoomId is required'
      });
    }
    if (this.preconnectPromise) {
      let state = 'pending';
      if (this.signalChannel?.isConnected) {
        state = 'connected';
      } else if (this.signalChannel?.isConnecting) {
        state = 'connecting';
      }
      this._log.warn(`preconnect is already ${state}, skip duplicate call`);
      return this.preconnectPromise;
    }

    this.userId = userId;
    this.sdkAppId = sdkAppId;
    this.userSig = userSig;
    this.roomId = String(roomId || strRoomId);
    this.useStringRoomId = Boolean(strRoomId && !roomId);
    this._log.setSdkAppId(this.sdkAppId);
    this._log.setUserId(this.userId);

    this.preconnectPromise = Promise.race([
      this.doPreconnect(sdkAppId, userId, userSig, frameWorkType, roomId, strRoomId),
      new Promise((resolve, reject) => {
        this.preconnectTimeoutId = setTimeout(() => {
          reject(new RtcError({
            code: ErrorCode.INVALID_OPERATION,
            message: `preconnect timeout after ${this.PRECONNECT_TIMEOUT}ms`
          }));
        }, this.PRECONNECT_TIMEOUT);
      })
    ])
      .then(() => {
        this.clearPreconnectTimeout();
      })
      .catch(error => {
        this.clearPreconnectTimeout();
        this.closePreconnect().catch(() => {});
        throw error;
      });
    return this.preconnectPromise;
  }

  private async doPreconnect(sdkAppId: number, userId: string, userSig: string, frameWorkType: number, roomId?: number, strRoomId?: string): Promise<void> {
    try {
      if (!this.proxy_ws && !this.proxy_wt && !this.scheduleResult.domains && !utils.getEnv()) {
        await this.schedule({
          sdkAppId,
          userId,
          userSig,
          roomId,
          strRoomId,
          role: UserRoleNumber.ANCHOR,
          privateMapKey: null,
          businessInfo: null,
          streamId: null,
          userDefineRecordId: null
        } as TRTCParams, frameWorkType);
      }
      if (!this.scheduleResult.config?.prelink) {
        throw new RtcError({
          code: ErrorCode.INVALID_OPERATION,
          message: 'preconnect failed: your sdkAppId is not supported, please contact us [https://trtc.io/contact] to enable it.'
        });
      }
      // 预连接 ws
      await this.initialize({ isPreconnect: true });
      this.signalChannel?.markPreconnectConnected({ sdkAppId, userId, userSig });
      // 超时后若未进房则关闭 ws. 后台正常 30s 关闭, 有 prelink 连接参数 60s 关闭, sdk 兜底 5min 关闭
      this.signalChannel?.stopPreconnectIn(this.PRECONNECT_EXPIRED_TIME / 1000);
      this._log.info('preconnect success');
    } catch (error) {
      this._log.error('preconnect failed', error);
      throw error;
    }
  }

  private clearPreconnectTimeout() {
    if (this.preconnectTimeoutId) {
      clearTimeout(this.preconnectTimeoutId);
      this.preconnectTimeoutId = null;
    }
  }

  async closePreconnect(): Promise<void> {
    if (!this.signalChannel?.hasPreconnect) return;
    if (this.isJoined) {
      this._log.warn('close preconnect failed: has joined room');
      return;
    }
    // keepAlive 优先级高于 preconnect
    if (this.signalChannel.keepAlive) {
      this._log.info('skip close preconnect: use keepAlive');
      return;
    }
    this.clearNetworkQuality();
    if (this.signalChannel && this.signalChannel.isConnected) {
      this.signalChannel.close();
      this.setSignalChannel(null);
    }
    this.clearPreconnectTimeout();
    this.preconnectPromise = null;
  }

  /**
   * 该方法用于根据业务侧的订阅状态，自动切换大小流。
   * 1. 业务侧不想订阅小流，无论远端是否有小流都不订阅小流。
   * 2. 业务侧想订阅小流，则根据远端是否有小流来订阅，有小流则订阅小流，无小流则订阅大流。
   */
  async checkSubscribeBigSmallVideo(connection: DownlinkConnection | DownlinkConnectionSPC) {
    const { subscribeState, userId, muteState: { hasSmall: isRemoteHasSmallVideo, hasVideo: isRemoteHasBigVideo } } = connection;
    // 远端没有推视频，直接 return
    if (!isRemoteHasSmallVideo && !isRemoteHasBigVideo) return;
    // 业务侧未订阅任何视频，直接 return，无需切换大小流
    if (!subscribeState.video && !subscribeState.smallVideo) return;

    const bigSmallRecord = this._changeBigSmallRecords.get(userId);
    if (!bigSmallRecord || bigSmallRecord.isSubscribing || bigSmallRecord.reSubscribeCount <= 0) return;

    const { options, reSubscribeCount } = bigSmallRecord;

    // 想订阅大流 && 已订阅大流 return
    // 想订阅小流 && 已订阅小流 && 远端有推小流 return
    // 想订阅小流 && 已订阅小流 && 远端没推流小流 则需要继续执行，切换成订阅大流
    if ((options.video && subscribeState.video) || (options.smallVideo && subscribeState.smallVideo && isRemoteHasSmallVideo)) {
      return;
    }

    // options 是 prefer 订阅的， subscribeOptions 是更新状态后需要订阅的
    const subscribeOptions = {
      audio: connection.remoteAudioTrack.isSubscribed || connection.remoteAudioTrack.isSubscribing,
      auxiliary: connection.remoteAuxiliaryTrack.isSubscribed || connection.remoteAuxiliaryTrack.isSubscribing,
      video: options.video,
      smallVideo: options.smallVideo,
      datachannel: connection.subscribeState.datachannel
    };

    try {
      if (!isRemoteHasSmallVideo && subscribeOptions.smallVideo) {
        subscribeOptions.video = true;
        subscribeOptions.smallVideo = false;
      }
      // 待订阅状态与已订阅状态相同，直接 return 不处理。
      if (subscribeOptions.smallVideo === subscribeState.smallVideo && subscribeOptions.video === subscribeState.video) return;

      bigSmallRecord.isSubscribing = true;
      bigSmallRecord.reSubscribeCount = reSubscribeCount - 1;

      await connection.subscribe(subscribeOptions, RemoteStreamType.Main);

      connection.remoteVideoTrack.setMediaType(subscribeOptions.smallVideo ? MediaType.SMALL_VIDEO : MediaType.BIG_VIDEO);

      this._log.info(`change [${userId}] to ${subscribeOptions.smallVideo ? 'small' : 'big'} video successfully. count ${SUBSCRIBE_SMALL_RETRY_COUNT - bigSmallRecord.reSubscribeCount}.`);

      bigSmallRecord.isSubscribing = false;
      bigSmallRecord.reSubscribeCount = SUBSCRIBE_SMALL_RETRY_COUNT;
    } catch (e) {
      this._log.info(`change [${userId}] to ${subscribeOptions.smallVideo ? 'small' : 'big'} video failed. count ${SUBSCRIBE_SMALL_RETRY_COUNT - bigSmallRecord.reSubscribeCount}. reason: ${e}`);
      bigSmallRecord.isSubscribing = false;

      // 归 0 就不重试了
      if (bigSmallRecord.reSubscribeCount === 0) {
        this._changeBigSmallRecords.delete(userId);
      }
    }
  }

  // 切换成小流 or 大流
  changeType(isSmall: boolean, user: User) {
    const options = {
      video: !isSmall,
      smallVideo: isSmall
    };
    const bigSmallRecord = {
      options,
      isSubscribing: false,
      reSubscribeCount: SUBSCRIBE_SMALL_RETRY_COUNT
    };
    this._changeBigSmallRecords.set(user.userId, bigSmallRecord);
    this._log.info(`set [${user.userId}] video prefer type: ${isSmall ? 'small' : 'big'}`);
    this.emit(RoomEvent.SUBSCRIBE_SMALL_VIDEO_CHANGED, { userId: user.userId, isSmall });
    const downlink = this.remotePublishedUserMap.get(user.userId);
    if (downlink) this.checkSubscribeBigSmallVideo(downlink);
  }

  /**
   * @private
   * @memberof Client
   */
  get smallStreamConfig() {
    return this._smallStreamConfig;
  }

  /**
   * _businessInfo 透传给后台，供部分内部客户使用，不暴露给接入侧
   * @private
   * @param {Object} options 初始化 Client 的参数
   * @memberof Client
   */
  _initBusinessInfo(options: any) {
    // business info pass through to remote server.
    this._businessInfo = options.businessInfo;

    let bzInfo: any = {};
    if (isString(options.businessInfo)) {
      bzInfo = JSON.parse(options.businessInfo);
    }
    if (!isUndefined(options.pureAudioPushMode)) {
      if (!Number.isInteger(Number(options.pureAudioPushMode))) {
        throw new RtcError({
          code: ErrorCode.INVALID_PARAMETER,
          message: generateErrorMessage({ key: ErrorName.INVALID_PURE_AUDIO })
        });
      }
      this._pureAudioPushMode = options.pureAudioPushMode;
      if (!bzInfo.Str_uc_params) {
        bzInfo.Str_uc_params = {};
      }
      bzInfo.Str_uc_params.pure_audio_push_mod = this._pureAudioPushMode;
    }

    if (!isUndefined(options.userDefineRecordId)) {
      const reg = /^[A-Za-z0-9_-]{1,64}$/gi;
      if (options.userDefineRecordId.match(reg) === null) {
        throw new RtcError({
          code: ErrorCode.INVALID_PARAMETER,
          message: generateErrorMessage({ key: ErrorName.INVALID_USER_DEFINE_RECORDID })
        });
      } else {
        if (!bzInfo.Str_uc_params) {
          bzInfo.Str_uc_params = {};
        }
        bzInfo.Str_uc_params.userdefine_record_id = options.userDefineRecordId;
      }
    }
    // 'userDefinePushArgs'字段只目前只开放给小鹅通
    if (!isUndefined(options.userDefinePushArgs)) {
      if (
        isString(options.userDefinePushArgs) &&
        String(options.userDefinePushArgs) &&
        String(options.userDefinePushArgs).length <= 256
      ) {
        if (!bzInfo.Str_uc_params) {
          bzInfo.Str_uc_params = {};
        }
        bzInfo.Str_uc_params.userdefine_push_args = options.userDefinePushArgs;
      } else {
        throw new RtcError({
          code: ErrorCode.INVALID_PARAMETER,
          message: generateErrorMessage({ key: ErrorName.INVALID_USER_DEFINE_PUSH_ARGS })
        });
      }
    }
    if (!isEmpty(bzInfo)) {
      // _businessInfo must be in JSON format
      this._businessInfo = JSON.stringify(bzInfo);
    }
  }

  sendCustomMessage(data: CustomMessageData) {
    this._customMessageManager?.send(data);
  }
  async enableInsertableStreams() {
    if (this.singlePC && !this.singlePC.enableInsertableStreams && IS_INSERTABLE_STREAM_SUPPORTED) {
      this.singlePC.enableInsertableStreams = true;
      await this.singlePC.waitForPeerConnectionConnected();
      return await this.singlePC.startReconnection();
    }
  }

  sendSignalMessage(param: SendWaitForResponseWithRetryParams) {
    if (!this.signalChannel) {
      return Promise.reject(new RtcError({ code: ErrorCode.INVALID_OPERATION, message: 'not join' }));
    }
    return this.signalChannel?.sendWaitForResponseWithRetry(param);
  }

  get enableCodecPipeline() {
    return this.videoManager.encodePipeline.length > 0 ||
      this.videoManager.decodePipeline.length > 0 ||
      this.audioManager.encodePipeline.length > 0 ||
      this.audioManager.decodePipeline.length > 0;
  }
  get scriptTransformWorker() {
    return this.singlePC?.scriptTransformWorker;
  }

  async switchRoom(options: SwitchRoomConfig) {
    if (!this.signalChannel || !this.singlePC) return;
    const { roomId, strRoomId, userSig, privateMapKey } = options;
    // 隐藏参数
    // @ts-ignore
    const autoSubscribeCount = this.scheduleResult.config?.autoSubscribeCount || options?.autoSubscribeCount || 1; // TODO: 云控
    const targetRoomId = String(this.useStringRoomId ? strRoomId : roomId);
    const ssrcGroups: SSRCGroup[] = [];
    for (let i = 0; i < autoSubscribeCount; i++) {
      ssrcGroups.push(buildSSRCGroup());
    }
    const data = {
      currRoomId: this.roomId,
      targetRoomId,
      useStringRoomId: this.useStringRoomId,
      ability: this.singlePC.clientAbility,
      userSig,
      privateMapKey: privateMapKey ?? this.privateMapKey, // 为空时复用上次进房的 privateMapKey
      subscribe: ssrcGroups
    };
    this.singlePC.autoSubscribedUserMap.clear();
    this.singlePC.autoSubscribedSsrcGroups.clear();
    this.singlePC.autoSubscribedSsrcGroups.set(targetRoomId, ssrcGroups);
    const originRoomId = this.roomId;
    // 提前设置 roomId，规避 cmd 23 比 4160 先到导致 subscribe 黑屏的问题 ref: https://tapd.woa.com/tapd_fe/20396022/story/detail/1020396022126413876
    this.roomId = (this.useStringRoomId ? strRoomId : String(roomId)) as string;
    this.switchRoomSubedReq = new Promise(resolve => {
      // resolve 的情况: 1. 收到 cmd 4161 2. 切换房间信令 5s 超时未响应 3. 切换房间失败
      this.resolveSwitchRoomSubedReq = resolve;
      delay(5000).then(resolve);
    });
    innerEmitter.emit(INNER_EVENT.SWITCH_ROOM_START, { room: this });
    await this.singlePC.waitForPeerConnectionConnected();
    let response: SignalResponse;
    try {
      this.userManager.clear();
      response = await this.signalChannel.sendWaitForResponse({
        command: SignalCommand.SWITCH_ROOM,
        responseCommand: SignalEvent.SEND_SWITCH_ROOM_RES,
        data
      });
      const { code, message } = response.data;
      if (code === 0) {
        this.userSig = userSig;
        if (!isUndefined(privateMapKey)) this.privateMapKey = privateMapKey;
        innerEmitter.emit(INNER_EVENT.SWITCH_ROOM_SUCCESS, { room: this, currentRoomId: originRoomId, targetRoomId });
      } else {
        this._log.error(`switch room failed. result: ${code} error: ${message}`);
        const error = new RtcError({
          code: ErrorCode.SWITCH_ROOM_FAILED,
          extraCode: code,
          message
        });
        innerEmitter.emit(INNER_EVENT.SWITCH_ROOM_FAILED, { room: this, error });
        throw error;
      }
    } catch (error) {
      this.singlePC.autoSubscribedSsrcGroups.clear();
      this.roomId = originRoomId; // 重置 roomId
      this.resolveSwitchRoomSubedReq();
      throw error;
    }
  }

  isSwitchRoomSupported() {
    const message = 'unable to use switchRoom API, fallback to exitRoom and enterRoom.';
    if (this.scheduleResult.config?.switchRoom !== true) {
      this._log.warn(`${message} Reason: this sdkAppId is not supported, please contact us [https://trtc.io/contact] to enable it.`);
      return false;
    }
    if (this.scene !== Scene.LIVE) {
      this._log.warn(`${message} Reason: the scene is not 'live'.`);
      return false;
    }
    if (this.role !== UserRole.AUDIENCE) {
      this._log.warn(`${message} Reason: the role is not 'audience'.`);
      return false;
    }
    if (!this.singlePC) {
      this._log.warn(`${message} Reason: is not using single peerConnection.`);
      return false;
    }
    return true;
  }

  requestRemoteFallbackToH264() {
    this.singlePC?.requestRemoteFallbackToH264();
  }
  startUpdateNTPTime() {
    if (!this.signalChannel) return;
    const promiseList = [];
    for (let i = 0; i < 5; i++) {
      promiseList.push(this.updateNTPTime());
    }
    return Promise.all(promiseList)
      .then(result => {
        // offset 振幅超过 30ms，则认为结果可能不准。
        let minOffset = result[0].offset;
        let maxOffset = result[0].offset;
        result.forEach(item => {
          minOffset = Math.min(item.offset, minOffset);
          maxOffset = Math.max(item.offset, maxOffset);
        });
        const averageRTT = Math.floor(result.reduce((sum, item) => sum + item.rtt, 0) / result.length);
        const averageOffset = Math.floor(result.reduce((sum, item) => sum + item.offset, 0) / result.length);
        if (maxOffset - minOffset > 30 || averageRTT > 50) {
          setTimeout(() => this.startUpdateNTPTime(), 5000);
        }
        setNetworkTimeOffset(averageOffset);
        this.scriptTransformWorker?.postMessage({ type: 'ntp-offset', data: averageOffset });
        this._log.debug(`ntp updated offset: ${averageOffset}`);
        this.emit(RoomEvent.NTP_TIME_UPDATED);
      })
      .catch(error => {
        this._log.warn(`ntp updated failed: ${error}`);
      });
  }

  updateNTPTime() {
    const sendTime = Date.now();
    return this.signalChannel!.sendWaitForResponse({
      command: SignalCommand.UPDATE_NETWORK_TIME,
      responseCommand: SignalEvent.UPDATE_NETWORK_TIME_RESULT,
      addReceiveTime: true, // 在收到回包后，打点计算 receiveTime，而不应该在 then 中计算 receiveTime。因为若起前面有其他微任务执行耗时长，导致 receiveTime 计算不正确
      data: {
        clientSendTime: String(sendTime)
      },
      enableLog: false
    })
      .then(result => {
        const serverSendTime = Number(result.data.data.serverSendTime);
        const serverRecvTime = Number(result.data.data.serverRecvTime);
        const receiveTime = result.data.receiveTime || Date.now();
        const offset = ((serverRecvTime - sendTime) + (serverSendTime - receiveTime)) / 2;
        const rtt = (receiveTime - sendTime) - (serverRecvTime - serverSendTime);
        return { rtt, offset };
      });
  }
}
